/**
 * Pages de pilotage : Résumé, Shopify, Souscriptions, Ads.
 *
 * ── Un composant, quatre vues ───────────────────────────────────────────────
 * Les quatre pages partagent tout : période, comparaisons, sélecteurs, et
 * surtout les calculs croisés (CA total = boutique + abonnements ; ROAS et
 * coûts d'acquisition = pub rapportée à la boutique et aux abonnements). Les
 * écrire une fois garantit que « Résumé » et « Ads » affichent le même ROAS.
 * Chaque vue ne lance que les requêtes dont elle a besoin (`enabled`).
 *
 * ── Deux requêtes, une page ─────────────────────────────────────────────────
 * Shopify et Stripe ont chacun leur route et leur worker : une panne de l'un
 * ne prive pas la page des chiffres de l'autre. Le CA total, qui a besoin des
 * deux, s'assemble ici et affiche un tiret tant qu'il manque une moitié.
 *
 * ── Chaque chiffre vient avec ses deux références ───────────────────────────
 * Période précédente (même longueur) et même période l'an dernier, sur chaque
 * tuile. La première dit si ça progresse, mais elle est sensible à la
 * saisonnalité — décembre contre novembre mélange l'effet des fêtes ; la
 * seconde neutralise la saison, au prix de douze mois de changements. Dès un
 * an de période, les deux désignent la même tranche : une seule est affichée.
 *
 * ── La journée en cours compte ──────────────────────────────────────────────
 * Le worker Shopify passe toutes les dix minutes : la période inclut
 * AUJOURD'HUI (portée `live`, cf. `lib/dates.ts`), contrairement à l'usage de
 * l'app qui s'arrête à la veille. Une journée entamée comparée à des journées
 * pleines tire les variations vers le bas le matin — c'est accepté, et la
 * tuile « Aujourd'hui, en cours » donne la lecture à la même heure.
 */

import {
  REFUND_BASES,
  REFUND_BASIS_LABELS,
  SUBSCRIPTION_VAT_PERCENT,
  adsRoas,
  attributedExclTax,
  TOP_COUNTRIES,
  type CountryPerformanceRow,
  type ProductPerformanceRow,
  type RefundBasis,
} from '@shared/types/sales';
import type { SubscriptionDeduction } from '@shared/types/settings';
import { useCallback, useMemo } from 'react';

import { AdsCampaignsTable } from '../components/AdsCampaignsTable';
import { AdsDailyTable } from '../components/AdsDailyTable';
import { CostRoasChart, type CostValuePoint } from '../components/CostRoasChart';
import { AdsPlatformChart } from '../components/AdsPlatformChart';
import { DEDUCTION_BASE_LABELS } from '../components/DeductionsEditor';
import { CountryAovChart } from '../components/CountryAovChart';
import { PlatformRevenueChart, type PlatformSeries } from '../components/PlatformRevenueChart';
import { RankedTrendChart } from '../components/RankedTrendChart';
import { SessionsCrChart } from '../components/SessionsCrChart';
import { ShopPerformanceTable, sumShopFigures } from '../components/ShopPerformanceTable';
import { SubscriptionsChurnChart } from '../components/SubscriptionsChurnChart';
import { RenewalMonthsTable } from '../components/RenewalMonthsTable';
import { SiteSubscriptionPrices } from '../components/SiteSubscriptionPrices';
import { SubscriptionWeeksTable } from '../components/SubscriptionWeeksTable';
import { AdsCostRatios, AdsPlatformSplit } from '../components/AdsPlatformSplit';
import { AdsKpiTable, type AdsKpiRow } from '../components/AdsKpiTable';
import { DonutChart, type DonutSlice } from '../components/DonutChart';
import { FinancialFunnel } from '../components/FinancialFunnel';
import { SalesTrendChart } from '../components/SalesTrendChart';
import { SectionShell } from '../components/SectionShell';
import { Bf26PlanTable } from '../components/Bf26PlanTable';
import { StatTile, type StatDelta, type StatStatus } from '../components/StatTile';
import { TopProductsTable } from '../components/TopProductsTable';
import { useAuth } from '../hooks/useAuth';
import { useBrandingStats } from '../hooks/useBranding';
import { useBtobStats } from '../hooks/useBtob';
import { useTrafficStats } from '../hooks/useTraffic';
import { useChargesStats } from '../hooks/useCharges';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { useAdsStats, useAmazonAdsStats, useAmazonStats, useGoogleAdsStats, useSalesStats, useSubscriptionSales } from '../hooks/usePeriodStats';
import { useSearchChoice } from '../hooks/useSearchChoice';
import { shopPaymentFeeOf, type ShopPaymentFee } from '@shared/types/settings';
import { useBusinessSettings } from '../hooks/useBusinessSettings';
import { useDeductions } from '../hooks/useDeductions';
import { useSearchMultiChoice } from '../hooks/useSearchMultiChoice';
import { formatCompact, formatDayLong, formatDayShort, formatDecimal, formatInstant, formatInteger, formatMoney, formatMoneyCompact, formatPercent } from '../lib/format';
import { rateDelta, volumeDelta, type ReferenceSpec } from '../lib/deltas';
import { combineDailyRevenue, relativeChange, type TotalRevenuePoint } from '../lib/sales';
import { dataPeriod } from '../lib/dates';
import { bf26Level, bf26Targets, BF26_TOLERANCE } from '../lib/bf26';
import { summaryMetrics, type SummaryMetrics } from '../lib/summary';
import { CATEGORICAL_COLORS, OTHER_COLOR } from '../lib/theme';
import { relativeVerdict } from '../lib/verdict';
import type { AmazonAdsDailyPoint, AmazonDailyPoint, AmazonTotals, CountryRevenueRow, SalesDailyPoint, SalesTotals, SalesWindow, SubscriptionDailyPoint, SubscriptionTotals, SubscriptionWindow } from '../types';

const CHART_REFERENCES = ['yoy', 'pop'] as const;

/**
 * Plateformes publicitaires de la page Ads. Chacune est rapportée au CANAL
 * qu'elle alimente : Meta à la boutique Shopify, Amazon Ads aux ventes Amazon.
 */
const AD_PLATFORMS = ['meta', 'amazon', 'google'] as const;
type AdPlatform = (typeof AD_PLATFORMS)[number];
const AD_PLATFORM_LABELS: Record<AdPlatform, string> = { meta: 'Meta', amazon: 'Amazon Ads', google: 'Google Ads' };

interface PlatformPoint {
  date: string;
  spend: number;
}
const platformSpendOf = (point: PlatformPoint) => point.spend;
type ChartReference = (typeof CHART_REFERENCES)[number];

const netRevenueOf = (point: SalesDailyPoint) => point.netRevenue;
const ordersOf = (point: SalesDailyPoint) => point.orders;
const totalRevenueOf = (point: TotalRevenuePoint) => point.netRevenue;
const subscriptionRevenueOf = (point: SubscriptionDailyPoint) => point.netRevenue;
const shopRevenueExclTaxOf = (point: SalesDailyPoint) => point.netRevenueExclTax;
const amazonRevenueOf = (point: AmazonDailyPoint) => point.revenue;
const amazonSpendOf = (point: AmazonAdsDailyPoint) => point.spend;
const amazonAttributedOf = (point: AmazonAdsDailyPoint) => point.attributedSales;

/** ROAS : « ×3,25 ». Deux décimales : entre ×1,9 et ×2,1 se joue la rentabilité. */
const ratioFormatter = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatRatio = (value: number) => `×${ratioFormatter.format(value)}`;

/** Noms de pays en français (`FR` → « France »), sans table à maintenir. */
const regionNames = new Intl.DisplayNames(['fr'], { type: 'region' });
const countryName = (code: string | null): string => {
  if (!code) return 'Non renseigné';
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
};

/** Site d'origine d'une commande : `google` → « Google », inconnu → « Inconnu ». */
const siteName = (site: string | null): string => (site ? site.charAt(0).toUpperCase() + site.slice(1) : 'Inconnu');

/** Une fenêtre d'abonnements sans aucune activité ne sert pas de référence. */
const hasSubscriptionActivity = (window: SubscriptionWindow | undefined): boolean =>
  Boolean(window && (window.totals.activeAtEnd > 0 || window.totals.activeAtStart > 0 || window.totals.revenue > 0));

export type SalesView = 'summary' | 'bf26' | 'shopify' | 'subscriptions' | 'amazon' | 'ads';

const VIEW_TITLES: Record<SalesView, string> = {
  summary: 'Résumé',
  bf26: 'BF 26',
  shopify: 'Shopify',
  subscriptions: 'Souscriptions',
  amazon: 'Amazon',
  ads: 'Ads',
};

export function SalesPage({ view }: { view: SalesView }) {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();
  const [refundBasis, setRefundBasis] = useSearchChoice<RefundBasis>('refunds', REFUND_BASES, 'refund_date');
  const [chartReference, setChartReference] = useSearchChoice<ChartReference>('vs', CHART_REFERENCES, 'yoy');
  const [platforms, togglePlatform] = useSearchMultiChoice<AdPlatform>('platforms', AD_PLATFORMS);
  const withMeta = platforms.includes('meta');
  const withAmazonAds = platforms.includes('amazon');
  const withGoogleAds = platforms.includes('google');

  const salesQuery = useSalesStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
    refundBasis,
  });
  const { data } = salesQuery;

  const subscriptionsQuery = useSubscriptionSales({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
    // Le CA total (Résumé) et le coût par nouvel abonné (Ads) en ont besoin.
    enabled: view !== 'shopify',
  });
  const subs = subscriptionsQuery.data;

  const adsQuery = useAdsStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
    // Shopify : les dépenses Meta entrent dans le MC2.
    enabled: view === 'summary' || view === 'bf26' || view === 'ads' || view === 'shopify',
  });
  const ads = adsQuery.data;

  // GA4 : sessions du site pour « GA4 metrics over time » (Résumé).
  const trafficQuery = useTrafficStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
    enabled: view === 'summary',
  });
  const traffic = trafficQuery.data;
  const ga4Ready = Boolean(traffic?.meta.historyImported);
  /** Jours de la période avec les sessions GA4 à la place des sessions Shopify. */
  const ga4SalesDaily = useMemo(() => {
    const current = data?.daily.current ?? [];
    if (!ga4Ready || !traffic) return current;
    const sessions = new Map(traffic.daily.current.map((day) => [day.date, day.sessions]));
    return current.map((day) => ({ ...day, sessions: sessions.get(day.date) ?? null }));
  }, [data?.daily, traffic, ga4Ready]);

  // Amazon : ventes et publicité, deux API, deux requêtes. Utilisées par le
  // Résumé (totaux, ROAS, répartitions), la page Amazon et la page Ads.
  const amazonQuery = useAmazonStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
    enabled: view === 'summary' || view === 'bf26' || view === 'amazon' || view === 'ads',
  });
  const amazon = amazonQuery.data;
  const amazonAdsQuery = useAmazonAdsStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
    enabled: view === 'summary' || view === 'bf26' || view === 'amazon' || view === 'ads',
  });
  const amazonAds = amazonAdsQuery.data;
  // Google Ads : même réponse qu'Amazon Ads. Résumé (dépenses, ROAS, marge),
  // page Ads, et MC2 de la page Shopify (la pub Google amène des ventes Shopify).
  const googleAdsQuery = useGoogleAdsStats({
    from: period.from,
    to: period.to,
    token,
    onUnauthorized: signOut,
    enabled: view === 'summary' || view === 'bf26' || view === 'ads' || view === 'shopify',
  });
  const googleAds = googleAdsQuery.data;
  // Amazon branché = au moins un sync abouti. Sinon il ne compte pour rien, et
  // le dit, au lieu d'afficher des zéros qui passeraient pour des ventes nulles.
  const amazonLive = Boolean(amazon?.meta.lastSyncAt);
  const amazonAdsLive = Boolean(amazonAds?.meta.lastSyncAt);
  const amazonTotals = amazonLive ? amazon?.comparison.current.totals : undefined;
  const amazonAdsTotals = amazonAdsLive ? amazonAds?.comparison.current.totals : undefined;
  const googleAdsLive = Boolean(googleAds?.meta.lastSyncAt);
  const googleAdsTotals = googleAdsLive ? googleAds?.comparison.current.totals : undefined;

  // BtoB : ventes saisies à la main, dans le Total et les Products du Résumé.
  const btobQuery = useBtobStats({ from: period.from, to: period.to, token, onUnauthorized: signOut, enabled: view === 'summary' || view === 'bf26' });
  const btob = btobQuery.data;
  const btobTotals = btob?.comparison.current.totals;

  // Branding (page Frais) : MC3 = MC2 − branding, sur le Résumé et la page Shopify.
  const brandingQuery = useBrandingStats({ from: period.from, to: period.to, token, onUnauthorized: signOut, enabled: view === 'summary' || view === 'shopify' });
  const brandingStats = brandingQuery.data;

  // Charges fixes (page Charges) : retranchées dans le « Résultat net » du Résumé.
  const chargesQuery = useChargesStats({ from: period.from, to: period.to, token, onUnauthorized: signOut, enabled: view === 'summary' });
  const chargesStats = chargesQuery.data;

  const currency = data?.currency ?? 'EUR';
  const money = useCallback((value: number) => formatMoney(value, currency), [currency]);
  const moneyAxis = useCallback((value: number) => formatMoneyCompact(value, currency), [currency]);

  const comparison = data?.comparison;
  const totals = comparison?.current.totals;
  const overlap = comparison?.comparisonsOverlap ?? false;

  /**
   * Une ligne de comparaison.
   *
   * Le relatif mène, la valeur de référence suit en retrait : « +12 % » seul
   * ne dit pas si on parle de 50 € ou de 50 000 €.
   *
   * Une fenêtre sans AUCUNE commande (boutique pas encore ouverte l'an
   * dernier, import de l'historique pas terminé) rend « aucune vente » —
   * différent d'une baisse, et différent de « stable ».
   */
  const delta = (
    label: string,
    reference: SalesWindow | undefined,
    pick: (t: SalesTotals) => number | null,
    format: (value: number) => string,
    higherIsBetter: boolean | null,
  ): StatDelta => {
    if (!reference || !totals || reference.totals.orders === 0) {
      return { text: `aucune vente ${label}`, verdict: 'insufficient', higherIsBetter };
    }
    const current = pick(totals);
    const ref = pick(reference.totals);
    if (current === null || ref === null) {
      return { text: `vs ${label}`, verdict: 'insufficient', higherIsBetter };
    }
    const change = relativeChange(current, ref);
    return {
      text:
        change === null
          ? `vs ${label}`
          : `${change >= 0 ? '+' : '−'}${Math.abs(change).toLocaleString('fr-FR')} % vs ${label}`,
      // Référence à zéro (aucun remboursement l'an dernier) : pas de relatif,
      // mais la valeur de référence reste dite.
      verdict: change === null ? (current === ref ? 'flat' : 'insufficient') : relativeVerdict(change),
      higherIsBetter,
      note: format(ref),
    };
  };

  /** Les deux comparaisons d'une tuile — une seule quand elles se confondent. */
  const deltas = (
    pick: (t: SalesTotals) => number | null,
    format: (value: number) => string,
    higherIsBetter: boolean | null = true,
  ): StatDelta[] =>
    overlap
      ? [delta("l'an dernier", comparison?.yearAgo, pick, format, higherIsBetter)]
      : [
          delta('période préc.', comparison?.previous, pick, format, higherIsBetter),
          delta("l'an dernier", comparison?.yearAgo, pick, format, higherIsBetter),
        ];

  // La journée en cours se compare à la même heure, J−7 et J−364.
  const today = data?.today;
  const todayDeltas: StatDelta[] = today
    ? [
        { label: 'J−7', ref: today.lastWeek.grossRevenue },
        { label: 'il y a 52 sem.', ref: today.lastYear.grossRevenue },
      ].map(({ label, ref }) => {
        const change = relativeChange(today.current.grossRevenue, ref);
        return {
          text:
            change === null
              ? `vs ${label}`
              : `${change >= 0 ? '+' : '−'}${Math.abs(change).toLocaleString('fr-FR')} % vs ${label}`,
          verdict: change === null ? 'insufficient' : relativeVerdict(change),
          higherIsBetter: true,
          note: money(ref),
        };
      })
    : [];

  // --- Abonnements ----------------------------------------------------------

  const subsComparison = subs?.comparison;
  const subsTotals = subsComparison?.current.totals;
  const subsOverlap = subsComparison?.comparisonsOverlap ?? overlap;

  /** Références d'une tuile d'abonnements — une seule quand elles se confondent. */
  const subsRefs = (): Array<{ spec: ReferenceSpec; window: SubscriptionWindow | undefined }> => {
    const yearAgo = {
      spec: { label: "l'an dernier", available: hasSubscriptionActivity(subsComparison?.yearAgo) },
      window: subsComparison?.yearAgo,
    };
    if (subsOverlap) return [yearAgo];
    return [
      {
        spec: { label: 'période préc.', available: hasSubscriptionActivity(subsComparison?.previous) },
        window: subsComparison?.previous,
      },
      yearAgo,
    ];
  };

  const subsVolume = (
    pick: (t: SubscriptionTotals) => number,
    format: (value: number) => string,
    higherIsBetter: boolean | null = true,
  ): StatDelta[] =>
    subsRefs().map(({ spec, window }) =>
      volumeDelta(spec, subsTotals ? pick(subsTotals) : null, window ? pick(window.totals) : null, format, higherIsBetter, 'aucun abonné'),
    );

  const subsRate = (pick: (t: SubscriptionTotals) => number | null, higherIsBetter: boolean): StatDelta[] =>
    subsRefs().map(({ spec, window }) =>
      rateDelta(spec, subsTotals ? pick(subsTotals) : null, window ? pick(window.totals) : null, higherIsBetter, 'pas de taux'),
    );

  const businessSettings = useBusinessSettings().data;
  const shippingCostPerOrder = businessSettings?.shippingCostPerOrder ?? null;
  // Shopify : les dix pays au plus gros CA HT, communs au tableau et au graphique.
  const topShopCountries = useMemo(
    () => [...(data?.countryPerformance ?? [])].sort((a, b) => b.netRevenueExclTax - a.netRevenueExclTax).slice(0, TOP_COUNTRIES),
    [data?.countryPerformance],
  );
  const topShopCountryCodes = useMemo(() => topShopCountries.map((row) => row.country), [topShopCountries]);
  // Sites d'origine : classés au CA HT, les dix premiers pour les courbes.
  const shopSites = useMemo(
    () => [...(data?.sitePerformance ?? [])].sort((a, b) => b.netRevenueExclTax - a.netRevenueExclTax),
    [data?.sitePerformance],
  );
  const topShopSiteKeys = useMemo(() => shopSites.slice(0, TOP_COUNTRIES).map((row) => row.site), [shopSites]);
  // Courbes par pays / par site : jours de la période et points (jour, entrée).
  const shopDates = useMemo(() => (data?.daily.current ?? []).map((day) => day.date), [data?.daily]);
  const countryDailyPoints = useMemo(
    () => (data?.countryDaily ?? []).map((point) => ({ date: point.date, key: point.country, netRevenueExclTax: point.netRevenueExclTax })),
    [data?.countryDaily],
  );
  const siteDailyPoints = useMemo(
    () => (data?.siteDaily ?? []).map((point) => ({ date: point.date, key: point.site, netRevenueExclTax: point.netRevenueExclTax })),
    [data?.siteDaily],
  );
  // Produits : classés au CA HT, les dix premiers pour les courbes.
  const shopProducts = useMemo(
    () => [...(data?.productPerformance ?? [])].sort((a, b) => b.netRevenueExclTax - a.netRevenueExclTax),
    [data?.productPerformance],
  );
  const topShopProductKeys = useMemo(() => shopProducts.slice(0, TOP_COUNTRIES).map((row) => row.productKey), [shopProducts]);
  const productTitles = useMemo(() => new Map(shopProducts.map((row) => [row.productKey, row.title])), [shopProducts]);
  const productTitle = useCallback((key: string | null) => (key ? productTitles.get(key) ?? key : 'Inconnu'), [productTitles]);
  const productDailyPoints = useMemo(
    () => (data?.productDaily ?? []).map((point) => ({ date: point.date, key: point.productKey, netRevenueExclTax: point.netRevenueExclTax })),
    [data?.productDaily],
  );

  // Retenues sur le CA des abonnements (page Souscriptions).
  const deductionList = useDeductions().data ?? [];
  // Frais de paiement de la boutique : Shopify Payments (page Frais), pas
  // Stripe — Stripe n'encaisse que les abonnements.
  const shopPaymentFee: ShopPaymentFee | null =
    businessSettings?.shopPaymentFeePercent != null
      ? { percent: businessSettings.shopPaymentFeePercent, perOrder: businessSettings.shopPaymentFeePerOrder ?? 0 }
      : null;

  /** MC1 d'une ligne (pays, site) : CA HT − Cogs − frais Shopify Payments (même règle que les tableaux). */
  const rowMc1 = (f: Pick<CountryPerformanceRow, 'grossRevenue' | 'orders' | 'netRevenueExclTax' | 'cogs'>) =>
    shopPaymentFee ? f.netRevenueExclTax - f.cogs - shopPaymentFeeOf(shopPaymentFee, f) : 0;
  /**
   * Parts d'un classement (pays, sites) : les dix premiers (couleur par rang,
   * comme les courbes), puis une part « Autres » pour tout le reste.
   */
  const rankedShareSlices = <T,>(
    all: readonly T[],
    top: readonly T[],
    keyOf: (row: T) => string | null,
    labelOf: (key: string | null) => string,
    othersLabel: string,
    pick: (row: T) => number,
  ): DonutSlice[] => {
    const topKeys = new Set(top.map(keyOf));
    const slices: DonutSlice[] = top.map((row, index) => ({
      key: keyOf(row) ?? 'none',
      label: labelOf(keyOf(row)),
      value: Math.round(pick(row) * 100) / 100,
      color: CATEGORICAL_COLORS[index] ?? OTHER_COLOR,
    }));
    const others = all.filter((row) => !topKeys.has(keyOf(row))).reduce((sum, row) => sum + pick(row), 0);
    if (Math.abs(others) >= 0.01) {
      slices.push({ key: 'others', label: othersLabel, value: Math.round(others * 100) / 100, color: OTHER_COLOR });
    }
    return slices;
  };
  const countryShareSlices = (pick: (row: CountryPerformanceRow) => number) =>
    rankedShareSlices(data?.countryPerformance ?? [], topShopCountries, (row) => row.country, countryName, 'Autres pays', pick);
  const productShareSlices = (pick: (row: ProductPerformanceRow) => number) =>
    rankedShareSlices(shopProducts, shopProducts.slice(0, TOP_COUNTRIES), (row) => row.productKey, productTitle, 'Autres produits', pick);
  type SiteRow = NonNullable<typeof data>['sitePerformance'][number];
  const siteShareSlices = (pick: (row: SiteRow) => number) =>
    rankedShareSlices(shopSites, shopSites.slice(0, TOP_COUNTRIES), (row) => row.site, siteName, 'Autres sites', pick);

  // --- Shopify : marges sur coûts variables (MC KPIs) ---------------------
  // MC1 = CA HT − Cogs − Stripe ; MC1' = MC1 − Shipping ; MC2 = MC1 − Ads ;
  // MC3 = MC2 − Branding (page Frais, lissé sur la période). Un réglage manquant rend `null`
  // plutôt qu'une marge gonflée.
  const round2 = (value: number) => Math.round(value * 100) / 100;
  const mcFor = (key: 'current' | 'previous' | 'yearAgo') => {
    const t = comparison?.[key].totals;
    if (!t) return null;
    // Pub qui amène des ventes Shopify : Meta, et Google Ads une fois branché.
    const adSpend = (ads?.comparison[key].totals.spend ?? 0) + (googleAdsLive ? (googleAds?.comparison[key].totals.spend ?? 0) : 0);
    const mc1 = shopPaymentFee ? round2(t.netRevenueExclTax - t.cogs - shopPaymentFeeOf(shopPaymentFee, t)) : null;
    const mc1Prime = mc1 !== null && shippingCostPerOrder !== null ? round2(mc1 - t.orders * shippingCostPerOrder) : null;
    const mc2 = mc1 !== null ? round2(mc1 - adSpend) : null;
    const branding = brandingStats?.comparison[key].total;
    const mc3 = mc2 !== null && branding !== undefined ? round2(mc2 - branding) : null;
    const share = (value: number | null) =>
      value !== null && t.netRevenueExclTax > 0 ? Math.round((value / t.netRevenueExclTax) * 1000) / 10 : null;
    return { ads: adSpend, mc1, mc1Prime, mc2, mc3, mc1Percent: share(mc1), mc1PrimePercent: share(mc1Prime), orders: t.orders };
  };
  type McView = NonNullable<ReturnType<typeof mcFor>>;
  const mcNow = mcFor('current');
  const mcRefs = (overlap
    ? [{ label: "l'an dernier", key: 'yearAgo' as const }]
    : [
        { label: 'période préc.', key: 'previous' as const },
        { label: "l'an dernier", key: 'yearAgo' as const },
      ]
  ).map(({ label, key }) => ({ label, view: mcFor(key) }));
  const mcVolume = (pick: (m: McView) => number | null): StatDelta[] =>
    mcRefs.map(({ label, view }) =>
      volumeDelta({ label, available: (view?.orders ?? 0) > 0 }, mcNow ? pick(mcNow) : null, view ? pick(view) : null, money, true, 'aucune vente'),
    );
  const mcRate = (pick: (m: McView) => number | null): StatDelta[] =>
    mcRefs.map(({ label, view }) =>
      rateDelta({ label, available: (view?.orders ?? 0) > 0 }, mcNow ? pick(mcNow) : null, view ? pick(view) : null, true, 'aucune vente'),
    );
  /** Return Rate : remboursements ÷ CA encaissé avant remboursements, en %. */
  const returnRate = (t: SalesTotals) => (t.grossRevenue > 0 ? Math.round((t.refunds / t.grossRevenue) * 1000) / 10 : null);

  // --- Abonnements vendus sur le site ---------------------------------------
  // Encaissés par Shopify, comptés avec les abonnements : le Résumé les retire
  // du canal Shopify (CA, marge), la page Shopify les garde (elle colle à
  // l'admin Shopify).
  const siteRevenueTtc = subsTotals?.siteRevenue ?? 0;
  const siteRevenueHt = round2(siteRevenueTtc / (1 + SUBSCRIPTION_VAT_PERCENT / 100));
  const shopNetRevenue = round2((totals?.netRevenue ?? 0) - siteRevenueTtc);

  // --- Souscriptions : retenues et Final HT ---------------------------------

  /** CA des Sales KPIs : le MRR TTC au dernier jour de la fenêtre, comme le Résumé. */
  const subsCaTtc = (t: SubscriptionTotals) => t.mrrAtEnd;
  const subsCaHt = (t: SubscriptionTotals) => Math.round((t.mrrAtEnd / (1 + SUBSCRIPTION_VAT_PERCENT / 100)) * 100) / 100;
  /** Montant d'une retenue sur une fenêtre : son % de la base choisie. */
  const deductionAmount = (deduction: SubscriptionDeduction, t: SubscriptionTotals) =>
    Math.round((((deduction.base === 'ht' ? subsCaHt(t) : subsCaTtc(t)) * deduction.percent) / 100) * 100) / 100;
  /** CA HT − toutes les retenues. */
  const finalExclTax = (t: SubscriptionTotals) =>
    Math.round((subsCaHt(t) - deductionList.reduce((sum, d) => sum + deductionAmount(d, t), 0)) * 100) / 100;

  /**
   * Cash d'abonnements réellement ENCAISSÉ dans la fenêtre : factures Stripe
   * payées + abonnements vendus sur le site − remboursements, à leur date. C'est
   * la base de la marge nette par canal et du Résultat : sur « Hier », le MRR
   * (un chiffre mensuel, au dernier jour) afficherait 15 k€ pour une journée
   * qui n'en a encaissé que quelques centaines.
   */
  const subsCashTtc = (t: SubscriptionTotals) => t.netRevenue;
  const subsCashHt = (t: SubscriptionTotals) => t.netRevenueExclTax;
  const cashDeductionAmount = (deduction: SubscriptionDeduction, t: SubscriptionTotals) =>
    Math.round((((deduction.base === 'ht' ? subsCashHt(t) : subsCashTtc(t)) * deduction.percent) / 100) * 100) / 100;
  /** Encaissé HT de la fenêtre − toutes les retenues. */
  const finalCashExclTax = (t: SubscriptionTotals) =>
    Math.round((subsCashHt(t) - deductionList.reduce((sum, d) => sum + cashDeductionAmount(d, t), 0)) * 100) / 100;

  const reference = chartReference === 'yoy' ? data?.daily.yearAgo : data?.daily.previous;
  const referenceLabel = chartReference === 'yoy' ? "Même période l'an dernier" : 'Période précédente';

  const subsReference = chartReference === 'yoy' ? subs?.daily.yearAgo : subs?.daily.previous;

  // --- Publicité ------------------------------------------------------------


  // --- Résumé : indicateurs de ventes ---------------------------------------

  // --- Distribution du revenu ----------------------------------------------

  // Une couleur par CANAL, fixe : Shopify reste rose, que Stripe le dépasse ou
  // non. Amazon et BtoB sont listés, sans part, jusqu'à leur branchement.
  // « Revenue over time per platform » : CA TTC net par jour et par canal,
  // mêmes couleurs que le camembert ci-dessous. BtoB : les factures à leur date.
  const platformRevenueSeries = useMemo<PlatformSeries[]>(() => {
    const mapOf = <P,>(points: readonly P[] | undefined, date: (p: P) => string, value: (p: P) => number) => {
      const map = new Map<string, number>();
      for (const point of points ?? []) map.set(date(point), (map.get(date(point)) ?? 0) + value(point));
      return map;
    };
    // Abonnements vendus sur le site : comptés côté Stripe, retirés de Shopify.
    const siteByDay = mapOf(subs?.daily.current, (p) => p.date, (p) => p.siteRevenue ?? 0);
    return [
      {
        key: 'shopify',
        label: 'Shopify',
        color: CATEGORICAL_COLORS[0],
        byDay: mapOf(data?.daily.current, (p) => p.date, (p) => p.netRevenue - (siteByDay.get(p.date) ?? 0)),
      },
      { key: 'stripe', label: 'Stripe', color: CATEGORICAL_COLORS[1], byDay: mapOf(subs?.daily.current, (p) => p.date, (p) => p.netRevenue) },
      {
        key: 'amazon',
        label: 'Amazon',
        color: CATEGORICAL_COLORS[2],
        byDay: amazonLive ? mapOf(amazon?.daily.current, (p) => p.date, (p) => p.revenue) : new Map(),
      },
      { key: 'btob', label: 'BtoB', color: CATEGORICAL_COLORS[3], byDay: mapOf(btob?.sales, (p) => p.invoiceDate, (p) => p.revenueTtc) },
    ];
  }, [data?.daily, subs?.daily, amazon?.daily, amazonLive, btob?.sales]);

  const channelSlices: DonutSlice[] = [
    { key: 'shopify', label: 'Shopify', value: shopNetRevenue, color: CATEGORICAL_COLORS[0] },
    { key: 'stripe', label: 'Stripe (abonnements)', value: subsTotals?.netRevenue ?? 0, color: CATEGORICAL_COLORS[1] },
    amazonLive
      ? { key: 'amazon', label: 'Amazon', value: amazonTotals?.revenue ?? 0, color: CATEGORICAL_COLORS[2] }
      : { key: 'amazon', label: 'Amazon', value: 0, color: CATEGORICAL_COLORS[2], note: 'à venir' },
    btob && (btob.knownStores.length > 0)
      ? { key: 'btob', label: 'BtoB', value: btobTotals?.revenueTtc ?? 0, color: CATEGORICAL_COLORS[3] }
      : { key: 'btob', label: 'BtoB', value: 0, color: CATEGORICAL_COLORS[3], note: 'aucune saisie' },
  ];

  // Les dix premiers pays DE LA PÉRIODE, puis « Autres » — les mêmes que le
  // tableau. Couleurs dans l'ordre validé, par rang : ce sont les parts
  // VOISINES qui doivent se distinguer, et elles le sont toujours dans cet
  // ordre. Le pays est nommé dans la légende et le tableau, jamais par la
  // seule couleur (qui, elle, peut changer d'une période à l'autre).
  // Pays : Shopify et Amazon additionnés, pays par pays.
  const mergedCountries: CountryRevenueRow[] = (() => {
    const byCode = new Map<string, CountryRevenueRow>();
    for (const row of [...(data?.countries ?? []), ...(amazonLive ? (amazon?.countries ?? []) : [])]) {
      const key = row.country ?? '';
      const existing = byCode.get(key);
      if (existing) {
        existing.current = Math.round((existing.current + row.current) * 100) / 100;
        existing.previous = Math.round((existing.previous + row.previous) * 100) / 100;
        existing.yearAgo = Math.round((existing.yearAgo + row.yearAgo) * 100) / 100;
      } else {
        byCode.set(key, { ...row });
      }
    }
    return [...byCode.values()].sort((a, b) => b.current - a.current);
  })();

  // Abonnements de la période (devenus payants dans la période, encore actifs à sa fin).
  const periodSubscriptions = subs?.periodSubscriptions ?? [];
  const periodByInterval = (interval: string) =>
    periodSubscriptions.filter((row) => row.billingInterval === interval).reduce((sum, row) => sum + row.subscribers, 0);

  /** Par pays : dix premiers, puis « Autres pays » (pays inconnu compris). */
  const subscriberCountrySlices: DonutSlice[] = (() => {
    const byCountry = new Map<string | null, number>();
    for (const row of periodSubscriptions) byCountry.set(row.country, (byCountry.get(row.country) ?? 0) + row.subscribers);
    const countries = [...byCountry.entries()]
      .map(([country, subscribers]) => ({ country, subscribers }))
      .filter((row) => row.subscribers > 0)
      .sort((a, b) => b.subscribers - a.subscribers);
    const known = countries.filter((row) => row.country !== null);
    const top = known.slice(0, TOP_COUNTRIES);
    const others =
      known.slice(TOP_COUNTRIES).reduce((sum, row) => sum + row.subscribers, 0) +
      countries.filter((row) => row.country === null).reduce((sum, row) => sum + row.subscribers, 0);
    const slices: DonutSlice[] = top.map((row, index) => ({
      key: row.country ?? 'none',
      label: countryName(row.country),
      value: row.subscribers,
      color: CATEGORICAL_COLORS[index] ?? OTHER_COLOR,
    }));
    if (others > 0) slices.push({ key: 'others', label: 'Autres pays', value: others, color: OTHER_COLOR });
    return slices;
  })();

  const countrySlices: DonutSlice[] = (() => {
    const countries = mergedCountries.filter((row) => row.current > 0);
    const top = countries.slice(0, TOP_COUNTRIES);
    const others = countries.slice(TOP_COUNTRIES).reduce((sum, row) => sum + row.current, 0);
    const slices: DonutSlice[] = top.map((row, index) => ({
      key: row.country ?? 'none',
      label: countryName(row.country),
      value: row.current,
      color: CATEGORICAL_COLORS[index] ?? OTHER_COLOR,
    }));
    if (others > 0) {
      slices.push({ key: 'others', label: 'Autres pays', value: Math.round(others * 100) / 100, color: OTHER_COLOR });
    }
    return slices;
  })();

  const ltv = subs?.ltv;
  const summaryNow = summaryMetrics({
    shop: totals,
    subscriptions: subsTotals,
    ads: ads?.comparison.current.totals,
    amazon: amazonTotals,
    amazonAds: amazonAdsTotals,
    btob: btobTotals,
    googleAds: googleAdsTotals,
    ltv,
    shopPaymentFee,
    subscriptionDeductions: deductionList,
    fixedCharges: chargesStats?.comparison.current.totals.total,
    branding: brandingStats?.comparison.current.total,
    shippingCostPerOrder,
  });
  const summaryRefs = (
    overlap
      ? [{ label: "l'an dernier", key: 'yearAgo' as const }]
      : [
          { label: 'période préc.', key: 'previous' as const },
          { label: "l'an dernier", key: 'yearAgo' as const },
        ]
  ).map(({ label, key }) => ({
    label,
    key,
    view: summaryMetrics({
      shop: comparison?.[key].totals,
      subscriptions: subsComparison?.[key].totals,
      ads: ads?.comparison[key].totals,
      amazon: amazonLive ? amazon?.comparison[key].totals : undefined,
      amazonAds: amazonAdsLive ? amazonAds?.comparison[key].totals : undefined,
      btob: btob?.comparison[key].totals,
      googleAds: googleAdsLive ? googleAds?.comparison[key].totals : undefined,
      ltv,
      shopPaymentFee,
      subscriptionDeductions: deductionList,
      fixedCharges: chargesStats?.comparison[key].totals.total,
      branding: brandingStats?.comparison[key].total,
      shippingCostPerOrder,
    }),
  }));

  /**
   * Comparaisons d'un indicateur du Résumé. Une référence sans vente (ou sans
   * pub, pour un ROAS) rend « aucune vente » / « aucune pub », pas un écart.
   */
  const summaryDeltas = (
    pick: (m: SummaryMetrics) => number | null,
    format: (value: number) => string,
    needsAds = false,
    higherIsBetter = true,
  ): StatDelta[] =>
    summaryRefs.map(({ label, key, view: ref }) => {
      const hasSales = (comparison?.[key].totals.orders ?? 0) > 0;
      const hasAds =
        (ads?.comparison[key].totals.spend ?? 0) > 0 ||
        (amazonAdsLive && (amazonAds?.comparison[key].totals.spend ?? 0) > 0) ||
        (googleAdsLive && (googleAds?.comparison[key].totals.spend ?? 0) > 0);
      return volumeDelta(
        { label, available: hasSales && (!needsAds || hasAds) },
        summaryNow ? pick(summaryNow) : null,
        ref ? pick(ref) : null,
        format,
        higherIsBetter,
        needsAds && hasSales ? 'aucune pub' : 'aucune vente',
      );
    });
  const adsTotals = ads?.comparison.current.totals;

  // --- Résumé : BF 26 ---------------------------------------------------------
  const bf26 = bf26Targets(period.from, period.to);
  const bf26Days = bf26 ? `${bf26.slices.reduce((n, { days }) => n + days, 0)} jour(s) du plan` : '';
  const bf26Slices = bf26 ? bf26.slices.map(({ slice, days }) => `${slice.label} (${days} j)`).join(', ') : '';
  const bf26Status = (actual: number, target: number, higherIsBetter: boolean, format: (x: number) => string): StatStatus => {
    const level = bf26Level(actual, target, higherIsBetter);
    const gap = Math.round(Math.abs(actual / target - 1) * 100);
    return {
      level,
      label: `cible ${format(target)} — ${level === 'good' ? 'atteinte' : `${gap} % ${higherIsBetter ? 'en dessous' : 'au-dessus'}`}`,
    };
  };

  // --- Résumé : marge nette par canal (HT, après pub) ----------------------
  //   Shopify     = CA HT − Cogs − frais Shopify Payments − Shipping − pub Meta − pub Google
  //   Abonnements = Final HT (CA HT − retenues : Sacem, Fees Stripe…)
  //   Amazon      = CA HT − COGS des kits (coût de revient de leur période) − pub Amazon
  //   BtoB        = CA HT − COGS des unités (idem)
  // Un réglage manquant laisse le canal sans part, avec la mention de ce qui
  // manque, plutôt qu'une marge gonflée.
  const netMarginSlices: DonutSlice[] = (() => {
    const slice = (key: string, label: string, color: string, value: number | null, missing: string): DonutSlice =>
      value === null
        ? { key, label, value: 0, color, note: missing }
        : { key, label, value: Math.round(value * 100) / 100, color };
    const shopify =
      totals && shopPaymentFee && shippingCostPerOrder !== null
        ? rowMc1({ ...totals, netRevenueExclTax: totals.netRevenueExclTax - siteRevenueHt }) -
          totals.orders * shippingCostPerOrder -
          (adsTotals?.spend ?? 0) -
          (googleAdsTotals?.spend ?? 0)
        : null;
    const amazonMargin =
      amazonTotals && amazonTotals.kitsWithoutCost === 0
        ? amazonTotals.revenueExclTax - amazonTotals.kitsCogs - (amazonAdsTotals?.spend ?? 0)
        : null;
    const btobMargin = btobTotals && btobTotals.unitsWithoutCost === 0 ? btobTotals.revenueHt - btobTotals.cogs : null;
    return [
      slice('shopify', 'Shopify', CATEGORICAL_COLORS[0], shopify, !shopPaymentFee ? 'frais Shopify Payments manquants (page Frais)' : 'coût d\'envoi manquant'),
      slice('stripe', 'Stripe (abonnements)', CATEGORICAL_COLORS[1], subsTotals ? finalCashExclTax(subsTotals) : null, 'en attente de Stripe'),
      slice('amazon', 'Amazon', CATEGORICAL_COLORS[2], amazonMargin, amazonLive ? 'coût de production manquant' : 'à venir'),
      slice('btob', 'BtoB', CATEGORICAL_COLORS[3], btobMargin, (btobTotals?.unitsWithoutCost ?? 0) > 0 ? 'coût de revient manquant' : 'aucune saisie'),
    ];
  })();
  /** Somme des marges connues ; les canaux sans chiffre sont nommés à part. */
  const netMarginTotal = Math.round(netMarginSlices.reduce((sum, slice) => sum + slice.value, 0) * 100) / 100;
  const netMarginIncomplete = netMarginSlices
    .filter((slice) => slice.note && !(slice.key === 'amazon' && !amazonLive) && !(slice.key === 'btob' && slice.note === 'aucune saisie'))
    .map((slice) => slice.label);

  // Séries de CA total : vides tant que les abonnements ne sont pas chargés,
  // plutôt qu'une courbe « totale » qui ne serait que la boutique.
  // Amazon ajouté jour par jour (mêmes fenêtres, même découpage à Paris).
  const withAmazon = (points: TotalRevenuePoint[], amazonDays: readonly { revenue: number }[] | undefined) =>
    amazonLive && amazonDays
      ? points.map((point, index) => ({
          ...point,
          netRevenue: Math.round((point.netRevenue + (amazonDays[index]?.revenue ?? 0)) * 100) / 100,
        }))
      : points;
  const amazonReference = chartReference === 'yoy' ? amazon?.daily.yearAgo : amazon?.daily.previous;
  const totalCurrent = subs ? withAmazon(combineDailyRevenue(data?.daily.current ?? [], subs.daily.current), amazon?.daily.current) : [];
  const totalReference = subs ? withAmazon(combineDailyRevenue(reference ?? [], subsReference ?? []), amazonReference) : [];

  // --- Amazon ----------------------------------------------------------------

  const amazonRefs = (overlap
    ? [{ label: "l'an dernier", key: 'yearAgo' as const }]
    : [
        { label: 'période préc.', key: 'previous' as const },
        { label: "l'an dernier", key: 'yearAgo' as const },
      ]);

  /** Comparaisons d'un indicateur de ventes Amazon. */
  const amazonDeltas = (
    pick: (t: AmazonTotals) => number | null,
    format: (value: number) => string,
    higherIsBetter: boolean | null = true,
  ): StatDelta[] =>
    amazonRefs.map(({ label, key }) => {
      const ref = amazon?.comparison[key].totals;
      return volumeDelta(
        { label, available: (ref?.orders ?? 0) > 0 },
        amazonTotals ? pick(amazonTotals) : null,
        ref ? pick(ref) : null,
        format,
        higherIsBetter,
        'aucune vente',
      );
    });

  /** Indicateurs publicitaires Amazon d'une fenêtre (TACoS = pub ÷ CA Amazon total). */
  const amazonAdsView = (key: 'current' | 'previous' | 'yearAgo') => {
    const adsTotalsWindow = amazonAds?.comparison[key].totals;
    // Tout HT : ventes attribuées ÷ 1,20, CA Amazon hors taxes, dépense déjà HT.
    const revenueHt = amazon?.comparison[key].totals.revenueExclTax ?? 0;
    if (!adsTotalsWindow) return null;
    const { spend, attributedSales, clicks } = adsTotalsWindow;
    const attributedHt = attributedExclTax(attributedSales);
    return {
      spend,
      attributedSales,
      acos: attributedHt > 0 ? Math.round((spend / attributedHt) * 1000) / 10 : null,
      roas: adsRoas(attributedSales, spend),
      tacos: revenueHt > 0 ? Math.round((spend / revenueHt) * 1000) / 10 : null,
      cpc: clicks > 0 ? Math.round((spend / clicks) * 100) / 100 : null,
    };
  };
  type AmazonAdsView = NonNullable<ReturnType<typeof amazonAdsView>>;
  const amazonAdsNow = amazonAdsView('current');
  const amazonAdsDeltas = (
    pick: (v: AmazonAdsView) => number | null,
    format: (value: number) => string,
    higherIsBetter: boolean | null,
    asRate = false,
  ): StatDelta[] =>
    amazonRefs.map(({ label, key }) => {
      const ref = amazonAdsView(key);
      const spec = { label, available: (ref?.spend ?? 0) > 0 };
      const now = amazonAdsNow ? pick(amazonAdsNow) : null;
      const before = ref ? pick(ref) : null;
      return asRate
        ? rateDelta(spec, now, before, higherIsBetter, 'aucune pub')
        : volumeDelta(spec, now, before, format, higherIsBetter, 'aucune pub');
    });

  const amazonMarketplaceSlices: DonutSlice[] = (amazon?.marketplaces ?? [])
    .filter((row) => row.revenue > 0)
    .slice(0, CATEGORICAL_COLORS.length)
    .map((row, index) => ({
      key: row.salesChannel,
      label: row.salesChannel,
      value: row.revenue,
      color: CATEGORICAL_COLORS[index] ?? OTHER_COLOR,
    }));

  const AD_PRODUCT_SHORT: Record<string, string> = {
    SPONSORED_PRODUCTS: 'SP',
    SPONSORED_BRANDS: 'SB',
    SPONSORED_DISPLAY: 'SD',
  };
  const amazonCampaignRows = (amazonAds?.campaigns ?? []).map((row) => ({
    campaignId: `${row.adProduct}:${row.campaignId}`,
    name: `[${AD_PRODUCT_SHORT[row.adProduct] ?? row.adProduct}] ${row.name}`,
    current: { spend: row.current.spend, metaPurchases: row.current.attributedOrders, metaPurchaseValue: row.current.attributedSales },
    previous: { spend: row.previous.spend, metaPurchases: row.previous.attributedOrders, metaPurchaseValue: row.previous.attributedSales },
    yearAgo: { spend: row.yearAgo.spend, metaPurchases: row.yearAgo.attributedOrders, metaPurchaseValue: row.yearAgo.attributedSales },
  }));
  const amazonAdsReference = chartReference === 'yoy' ? amazonAds?.daily.yearAgo : amazonAds?.daily.previous;

  /** Bloc publicité Amazon : sur la page Amazon ET sur la page Ads. */
  const amazonAdsBlock = (
    <>
      {amazonAdsQuery.error && (
        <p className="notice notice--error" role="alert">Publicité Amazon indisponible : {amazonAdsQuery.error.message}</p>
      )}
      {amazonAds && !amazonAdsLive && (
        <p className="notice">
          Publicité Amazon pas encore branchée : aucune synchronisation n'a abouti. Vérifier le conteneur{' '}
          <code>sync_amazon_ads</code> (<code>docker compose logs sync_amazon_ads</code>).
        </p>
      )}
      {amazonAdsLive && amazonAds?.meta.lastSyncFailed && (
        <p className="notice notice--error" role="alert">
          Le dernier passage du sync Amazon Ads a échoué. Chiffres du {formatInstant(amazonAds.meta.lastSyncAt)}.
        </p>
      )}
      {amazonAdsLive && amazonAds?.meta.firstDay && amazonAds.meta.firstDay > (amazonAds.comparison.yearAgo.to ?? '') && (
        <p className="notice">
          Amazon ne conserve qu'environ trois mois d'historique publicitaire : données depuis le{' '}
          {formatDayLong(amazonAds.meta.firstDay)}. Les comparaisons antérieures seront disponibles au fil du temps.
        </p>
      )}
      {amazonAdsLive && amazonAds && (
        <div className={amazonAdsQuery.isFetching ? 'is-refetching' : undefined}>
          <div className="kpi-row">
            <StatTile
              label="Dépenses Amazon Ads (HT)"
              value={money(amazonAdsNow?.spend ?? 0)}
              deltas={amazonAdsDeltas((v) => v.spend, money, null)}
              hint={
                amazonAdsNow?.cpc != null
                  ? `${formatInteger(amazonAdsTotals?.clicks ?? 0)} clics · ${formatMoney(amazonAdsNow.cpc, currency, true)} le clic`
                  : 'Sponsored Products, Brands et Display'
              }
            />
            <StatTile
              label="Ventes attribuées (TTC)"
              value={money(amazonAdsNow?.attributedSales ?? 0)}
              deltas={amazonAdsDeltas((v) => v.attributedSales, money, true)}
              hint={`${formatDecimal(amazonAdsTotals?.attributedOrders ?? 0)} commandes attribuées par Amazon`}
            />
            <StatTile
              label="ROAS Amazon (HT)"
              value={amazonAdsNow?.roas != null ? formatRatio(amazonAdsNow.roas) : '—'}
              deltas={amazonAdsDeltas((v) => v.roas, formatRatio, true)}
              hint="ventes attribuées ÷ dépenses"
            />
            <StatTile
              label="ACoS (HT)"
              value={amazonAdsNow?.acos != null ? formatPercent(amazonAdsNow.acos) : '—'}
              deltas={amazonAdsDeltas((v) => v.acos, formatPercent, false, true)}
              hint="dépenses ÷ ventes attribuées — plus bas, mieux c'est"
            />
            <StatTile
              label="TACoS (HT)"
              value={amazonAdsNow?.tacos != null ? formatPercent(amazonAdsNow.tacos) : '—'}
              deltas={amazonAdsDeltas((v) => v.tacos, formatPercent, false, true)}
              hint="dépenses ÷ CA Amazon TOTAL (organique compris)"
            />
          </div>
          <div className="chart-grid">
            <SalesTrendChart
              title="Dépenses Amazon Ads (HT)"
              subtitle="Toutes campagnes, converties en euros au taux BCE du jour"
              current={amazonAds.daily.current}
              reference={amazonAdsReference ?? []}
              referenceLabel={referenceLabel}
              value={amazonSpendOf}
              formatValue={money}
              formatAxis={moneyAxis}
            />
            <SalesTrendChart
              title="Ventes attribuées Amazon Ads (TTC)"
              subtitle="7 jours après le clic (Products), 14 jours (Brands, Display)"
              current={amazonAds.daily.current}
              reference={amazonAdsReference ?? []}
              referenceLabel={referenceLabel}
              value={amazonAttributedOf}
              formatValue={money}
              formatAxis={moneyAxis}
            />
            <AdsCampaignsTable rows={amazonCampaignRows} currency={currency} comparisonsOverlap={overlap} platform="Amazon" />
          </div>
        </div>
      )}
    </>
  );

  // --- Ads : rendement des plateformes cochées --------------------------------

  /**
   * Totaux d'une fenêtre sur les plateformes cochées. Chaque plateforme apporte
   * sa dépense, ses ventes attribuées, et le CA du canal qu'elle alimente.
   * Amazon non branché compte pour zéro.
   */
  const platformTotals = (key: 'current' | 'previous' | 'yearAgo') => {
    const metaAds = withMeta ? ads?.comparison[key].totals : undefined;
    const amzAds = withAmazonAds && amazonAdsLive ? amazonAds?.comparison[key].totals : undefined;
    const gAds = withGoogleAds && googleAdsLive ? googleAds?.comparison[key].totals : undefined;
    // Meta et Google Ads amènent des ventes Shopify, Amazon Ads des ventes Amazon.
    const shop = withMeta || (withGoogleAds && googleAdsLive) ? comparison?.[key].totals : undefined;
    const amz = withAmazonAds && amazonLive ? amazon?.comparison[key].totals : undefined;
    const spend = (metaAds?.spend ?? 0) + (amzAds?.spend ?? 0) + (gAds?.spend ?? 0);
    // Tout HT : ventes attribuées (TTC chez les régies) ÷ 1,20, CA des canaux hors taxes, dépense déjà HT.
    const attributedTtc = (metaAds?.metaPurchaseValue ?? 0) + (amzAds?.attributedSales ?? 0) + (gAds?.attributedSales ?? 0);
    const attributed = attributedExclTax(attributedTtc);
    const channelRevenue = (shop?.netRevenueExclTax ?? 0) + (amz?.revenueExclTax ?? 0);
    const orders = (shop?.orders ?? 0) + (amz?.orders ?? 0);
    const round = (value: number) => Math.round(value * 100) / 100;
    return {
      spend: round(spend),
      attributedTtc: round(attributedTtc),
      attributed: round(attributed),
      channelRevenue: round(channelRevenue),
      orders,
      roasAttributed: spend > 0 ? round(attributed / spend) : null,
      roasGlobal: spend > 0 ? round(channelRevenue / spend) : null,
      afterAds: round(channelRevenue - spend),
      costPerOrder: orders > 0 && spend > 0 ? round(spend / orders) : null,
    };
  };
  type PlatformTotals = ReturnType<typeof platformTotals>;
  const platformNow = platformTotals('current');
  const platformDeltas = (
    pick: (t: PlatformTotals) => number | null,
    format: (value: number) => string,
    higherIsBetter: boolean | null,
  ): StatDelta[] =>
    amazonRefs.map(({ label, key }) => {
      const ref = platformTotals(key);
      return volumeDelta({ label, available: ref.spend > 0 }, pick(platformNow), pick(ref), format, higherIsBetter, 'aucune pub');
    });

  /** Dépenses par jour des plateformes cochées, additionnées par rang (mêmes fenêtres). */
  const platformSpendSeries = (key: 'current' | 'previous' | 'yearAgo'): PlatformPoint[] => {
    const metaDays = withMeta ? (ads?.daily[key] ?? []) : [];
    const amzDays = withAmazonAds && amazonAdsLive ? (amazonAds?.daily[key] ?? []) : [];
    const gDays = withGoogleAds && googleAdsLive ? (googleAds?.daily[key] ?? []) : [];
    const base = [metaDays, amzDays, gDays].reduce<ReadonlyArray<{ date: string }>>((longest, days) => (days.length > longest.length ? days : longest), []);
    return base.map((day, index) => ({
      date: day.date,
      spend: Math.round(((metaDays[index]?.spend ?? 0) + (amzDays[index]?.spend ?? 0) + (gDays[index]?.spend ?? 0)) * 100) / 100,
    }));
  };
  /** Ads KPIs d'une plateforme, dans une fenêtre. Amazon non branché : rien. */
  const kpiRowsFor = (key: 'current' | 'previous' | 'yearAgo'): AdsKpiRow[] => {
    const rows: AdsKpiRow[] = [];
    const metaAds = ads?.comparison[key].totals;
    if (withMeta && metaAds) {
      rows.push({
        key: 'meta',
        platform: 'Meta',
        cost: metaAds.spend,
        impressions: metaAds.impressions,
        clicks: metaAds.clicks,
        conversions: metaAds.metaPurchases,
        conversionValue: metaAds.metaPurchaseValue,
      });
    }
    const amzAds = amazonAdsLive ? amazonAds?.comparison[key].totals : undefined;
    if (withAmazonAds && amzAds) {
      rows.push({
        key: 'amazon',
        platform: 'Amazon Ads',
        cost: amzAds.spend,
        impressions: amzAds.impressions,
        clicks: amzAds.clicks,
        conversions: amzAds.attributedOrders,
        conversionValue: amzAds.attributedSales,
      });
    }
    const gAds = googleAdsLive ? googleAds?.comparison[key].totals : undefined;
    if (withGoogleAds && gAds) {
      rows.push({
        key: 'google',
        platform: 'Google Ads',
        cost: gAds.spend,
        impressions: gAds.impressions,
        clicks: gAds.clicks,
        conversions: gAds.attributedOrders,
        conversionValue: gAds.attributedSales,
      });
    }
    return rows;
  };

  /** Les mêmes KPI, sommés sur les plateformes cochées ; taux recalculés sur les sommes. */
  const kpiTotals = (key: 'current' | 'previous' | 'yearAgo') => {
    const sum = kpiRowsFor(key).reduce(
      (acc, row) => ({
        cost: acc.cost + row.cost,
        impressions: acc.impressions + row.impressions,
        clicks: acc.clicks + row.clicks,
        conversions: acc.conversions + row.conversions,
        conversionValue: acc.conversionValue + row.conversionValue,
      }),
      { cost: 0, impressions: 0, clicks: 0, conversions: 0, conversionValue: 0 },
    );
    return {
      ...sum,
      ctr: sum.impressions > 0 ? Math.round((sum.clicks / sum.impressions) * 10000) / 100 : null,
      cvr: sum.clicks > 0 ? Math.round((sum.conversions / sum.clicks) * 10000) / 100 : null,
      roas: adsRoas(sum.conversionValue, sum.cost),
      cpc: sum.clicks > 0 ? Math.round((sum.cost / sum.clicks) * 100) / 100 : null,
      cpm: sum.impressions > 0 ? Math.round((sum.cost / sum.impressions) * 1000 * 100) / 100 : null,
      cpa: sum.conversions > 0 ? Math.round((sum.cost / sum.conversions) * 100) / 100 : null,
    };
  };
  type KpiTotals = ReturnType<typeof kpiTotals>;
  const kpiNow = kpiTotals('current');
  const kpiDeltas = (
    pick: (t: KpiTotals) => number | null,
    format: (value: number) => string,
    higherIsBetter: boolean | null,
    asRate = false,
  ): StatDelta[] =>
    amazonRefs.map(({ label, key }) => {
      const ref = kpiTotals(key);
      const spec = { label, available: ref.cost > 0 || ref.impressions > 0 };
      return asRate
        ? rateDelta(spec, pick(kpiNow), pick(ref), higherIsBetter, 'aucune pub')
        : volumeDelta(spec, pick(kpiNow), pick(ref), format, higherIsBetter, 'aucune pub');
    });

  /** Cost et valeur attribuée par jour, plateformes cochées additionnées (mêmes fenêtres). */
  const costValueSeries: CostValuePoint[] = (() => {
    const metaDays = withMeta ? (ads?.daily.current ?? []) : [];
    const amzDays = withAmazonAds && amazonAdsLive ? (amazonAds?.daily.current ?? []) : [];
    const gDays = withGoogleAds && googleAdsLive ? (googleAds?.daily.current ?? []) : [];
    const base = [metaDays, amzDays, gDays].reduce<ReadonlyArray<{ date: string }>>((longest, days) => (days.length > longest.length ? days : longest), []);
    return base.map((day, index) => ({
      date: day.date,
      cost: (metaDays[index]?.spend ?? 0) + (amzDays[index]?.spend ?? 0) + (gDays[index]?.spend ?? 0),
      value: (metaDays[index]?.metaPurchaseValue ?? 0) + (amzDays[index]?.attributedSales ?? 0) + (gDays[index]?.attributedSales ?? 0),
    }));
  })();

  const selectedPlatformsLabel = platforms.map((platform) => AD_PLATFORM_LABELS[platform]).join(' + ');

  const meta = data?.meta;
  const subsMeta = subs?.meta;

  // La page attend SA source ; les autres complètent sans bloquer l'affichage.
  const primary =
    view === 'subscriptions' ? subscriptionsQuery : view === 'ads' ? adsQuery : view === 'amazon' ? amazonQuery : salesQuery;
  const primaryLastSync =
    view === 'subscriptions'
      ? subsMeta?.lastSyncAt
      : view === 'ads'
        ? ads?.meta.lastSyncAt
        : view === 'amazon'
          ? amazon?.meta.lastSyncAt
          : meta?.lastSyncAt;
  const titleSub =
    view === 'summary'
      ? 'Boutique, abonnements et publicité'
      : view === 'bf26'
        ? 'Plan média Black Friday / Noël 2026 : la période face aux cibles'
      : view === 'shopify'
        ? `Boutique — Shopify, à jour au ${formatInstant(meta?.lastSyncAt ?? null)}`
        : view === 'subscriptions'
          ? `Abonnements — Stripe${subsMeta ? `, à jour au ${formatInstant(subsMeta.lastSyncAt)}` : ''}`
          : view === 'amazon'
            ? `Ventes et publicité Amazon${amazon?.meta.lastSyncAt ? `, à jour au ${formatInstant(amazon.meta.lastSyncAt)}` : ''}`
            : `Publicité — Meta${ads?.meta.accountName ? ` (${ads.meta.accountName})` : ''}${
              ads?.meta.lastSyncAt ? `, à jour au ${formatInstant(ads.meta.lastSyncAt)}` : ''
            }`;
  const showShop = view === 'summary' || view === 'shopify';

  return (
    <SectionShell
      lastSyncAt={primaryLastSync}
      isPending={primary.isPending}
      isFetching={primary.isFetching}
      error={primary.error}
      hasData={Boolean(primary.data)}
      title={
        <h1 className="page-title">
          {VIEW_TITLES[view]}
          <span className="page-title__sub">{titleSub}</span>
        </h1>
      }
    >
      {/* Etat du sync : dit, pas devine. Un historique pas encore importe
          fausse toutes les comparaisons, un sync en echec fige les chiffres. */}
      {showShop && meta && !meta.lastSyncAt && (
        <p className="notice">
          Aucune synchronisation Shopify n'a encore abouti. Vérifier que le conteneur{' '}
          <code>sync_shopify</code> tourne (<code>docker compose logs sync_shopify</code>).
        </p>
      )}
      {showShop && meta?.lastSyncAt && !meta.historyImported && (
        <p className="notice">
          Import de l'historique Shopify en cours : les comparaisons avec le passé seront fausses
          tant qu'il n'est pas terminé.
        </p>
      )}
      {showShop && meta?.lastSyncFailed && (
        <p className="notice notice--error" role="alert">
          Le dernier passage du sync Shopify a échoué. Les chiffres sont ceux du{' '}
          {formatInstant(meta.lastSyncAt)}.
        </p>
      )}

      {/* Souscriptions : aucune courbe pour l'instant, rien à comparer. */}
      {view !== 'subscriptions' && view !== 'bf26' && (
      <div className="filters">
        <div className="field">
          <span className="field__label">Courbes comparées à</span>
          <div className="preset-group" role="group" aria-label="Référence des courbes">
            <button
              type="button"
              className="preset"
              aria-pressed={chartReference === 'yoy'}
              onClick={() => setChartReference('yoy')}
            >
              L'an dernier
            </button>
            <button
              type="button"
              className="preset"
              aria-pressed={chartReference === 'pop'}
              onClick={() => setChartReference('pop')}
            >
              Période précédente
            </button>
          </div>
        </div>

      </div>
      )}

      {/* Ventes : le total (HT d'abord, c'est ce qui reste), les trois lectures
          du ROAS (toutes HT), la LTV qui nourrit le blended, puis ce qui a été
          vendu et la part de chaque famille dans le CA. */}
      {/* BF 26 : la période choisie face au plan média Black Friday / Noël 2026.
          Cibles au prorata des jours de chaque tranche du plan (cf. lib/bf26). */}
      {view === 'bf26' && !bf26 && (
        <p className="notice">La période choisie ne touche pas le plan BF 26 (1er nov. – 31 déc. 2026).</p>
      )}
      {view === 'bf26' && bf26 && (
        <>
          <div className="kpi-row">
            <StatTile
              label="Total HT"
              value={summaryNow?.totalHt != null ? money(summaryNow.totalHt) : '—'}
              hint={`Total HT de la période (comme dans Ventes). Plan : ${money(Math.round(bf26.revenueHt))} de CA produits HT attendu sur ces jours`}
            />
            <StatTile
              label="Commandes"
              value={summaryNow ? formatInteger(summaryNow.cpaPaidKits) : '—'}
              status={summaryNow ? bf26Status(summaryNow.cpaPaidKits, bf26.orders, true, (x) => formatInteger(Math.round(x))) : undefined}
              hint={`Piano LED payants vendus (Shopify${amazonLive ? ' + Amazon' : ''}, kits à 0 € d'un pack compris, hors commandes gratuites et BtoB) — les mêmes que le CPA — face aux commandes cibles du plan sur ${bf26Days} — ${bf26Slices}. Vert : cible atteinte ; orange : à moins de ${BF26_TOLERANCE * 100} % ; rouge : au-delà`}
            />
            <StatTile
              label="MER (HT)"
              value={summaryNow?.roasGlobal != null ? formatRatio(summaryNow.roasGlobal) : '—'}
              status={summaryNow?.roasGlobal != null ? bf26Status(summaryNow.roasGlobal, bf26.mer, true, formatRatio) : undefined}
              hint={`MER du Résumé face au MER cible du plan BF 26 sur ${bf26Days} — ${bf26Slices}. Vert : cible atteinte ; orange : à moins de ${BF26_TOLERANCE * 100} % ; rouge : au-delà`}
            />
            <StatTile
              label="CPA (HT)"
              value={summaryNow?.cpa != null ? formatMoney(summaryNow.cpa, currency, true) : '—'}
              status={summaryNow?.cpa != null ? bf26Status(summaryNow.cpa, bf26.cpa, false, (x) => formatMoney(x, currency, true)) : undefined}
              hint={`CPA du Résumé face au CPA cible du plan BF 26 sur ${bf26Days} — ${bf26Slices}. Vert : cible tenue ; orange : à moins de ${BF26_TOLERANCE * 100} % au-dessus ; rouge : au-delà`}
            />
          </div>
          {bf26.daysOutside > 0 && (
            <p className="kpi__hint">
              {bf26.daysOutside} jour{bf26.daysOutside > 1 ? 's' : ''} de la période hors plan BF 26 (1er nov. – 31 déc.) : ils comptent dans les chiffres, pas dans les cibles.
            </p>
          )}
        </>
      )}
      {view === 'bf26' && (
        <div className="chart-grid">
          <Bf26PlanTable activeLabels={new Set(bf26?.slices.map(({ slice }) => slice.label) ?? [])} />
        </div>
      )}

      {view === 'summary' && (
      <>

      <h2 className="section-heading">Ventes</h2>
      <div className="kpi-row">
        <StatTile
          label="Total HT"
          value={summaryNow?.totalHt != null ? money(summaryNow.totalHt) : '—'}
          deltas={summaryNow?.totalHt != null ? summaryDeltas((m) => m.totalHt, money) : undefined}
          hint={
            summaryNow?.totalHt != null
              ? `Piano LED ${money(summaryNow.kits.ht)} + pianos ${money(summaryNow.pianos.ht)} + abonnements ${money(summaryNow.subscriptions?.ht ?? 0)} + autres ${money(summaryNow.others.ht)}`
              : subscriptionsQuery.error
                ? 'indisponible — abonnements en erreur'
                : 'en attente des abonnements'
          }
        />
        <StatTile
          label="Total TTC"
          value={summaryNow?.totalTtc != null ? money(summaryNow.totalTtc) : '—'}
          deltas={summaryNow?.totalTtc != null ? summaryDeltas((m) => m.totalTtc, money) : undefined}
          hint={
            summaryNow?.totalTtc != null
              ? `Piano LED ${money(summaryNow.kits.ttc)} + pianos ${money(summaryNow.pianos.ttc)} + abonnements ${money(summaryNow.subscriptions?.ttc ?? 0)} + autres ${money(summaryNow.others.ttc)}`
              : 'en attente des abonnements'
          }
        />
        <StatTile
          label="MER (HT)"
          value={summaryNow?.roasGlobal != null ? formatRatio(summaryNow.roasGlobal) : '—'}
          deltas={summaryNow?.roasGlobal != null ? summaryDeltas((m) => m.roasGlobal, formatRatio, true) : undefined}
          hint={
            summaryNow?.spendTotal != null
              ? `Marketing Efficiency Ratio, HT — ${money(summaryNow.roasKitsHt)} de Piano LED (Shopify${amazonLive ? ' + Amazon' : ''}) ÷ ${money(summaryNow.spendTotal)} de pub, toutes plateformes`
              : adsQuery.error
                ? 'indisponible — pub en erreur'
                : 'en attente de Meta'
          }
        />
        <StatTile
          label="CPA (HT)"
          value={summaryNow?.cpa != null ? formatMoney(summaryNow.cpa, currency, true) : '—'}
          deltas={summaryNow?.cpa != null ? summaryDeltas((m) => m.cpa, (x) => formatMoney(x, currency, true), true, false) : undefined}
          hint={
            summaryNow?.spendTotal != null
              ? summaryNow.cpaPaidKits > 0
                ? `Coût par achat — ${money(summaryNow.spendTotal)} de pub, toutes plateformes ÷ ${formatInteger(summaryNow.cpaPaidKits)} Piano LED payants vendus (Shopify${amazonLive ? ' + Amazon' : ''}, kits à 0 € d'un pack compris, hors commandes gratuites et BtoB)`
                : 'aucun Piano LED payant vendu sur la période'
              : adsQuery.error
                ? 'indisponible — pub en erreur'
                : 'en attente de Meta'
          }
        />
        <StatTile
          label="MC2 sans abo (HT)"
          value={summaryNow?.mc2 ? money(summaryNow.mc2.margin) : '—'}
          deltas={summaryNow?.mc2 ? summaryDeltas((m) => m.mc2?.margin ?? null, money, true) : undefined}
          hint={
            summaryNow?.mc2
              ? `Piano LED + pianos ${money(summaryNow.mc2.revenueHt)} HT − coût de revient ${money(summaryNow.mc2.cogs)} − frais Shopify Payments ${money(summaryNow.mc2.paymentFees)} − pub ${money(summaryNow.mc2.adSpend)}`
              : 'voir la tuile MC2 %'
          }
        />
        <StatTile
          label="MC2 avec abo (HT)"
          value={summaryNow?.mc2WithSubscriptions ? money(summaryNow.mc2WithSubscriptions.margin) : '—'}
          deltas={summaryNow?.mc2WithSubscriptions ? summaryDeltas((m) => m.mc2WithSubscriptions?.margin ?? null, money, true) : undefined}
          hint={
            summaryNow?.mc2WithSubscriptions
              ? `MC2 sans abo + abonnements encaissés ${money(summaryNow.mc2WithSubscriptions.subscriptionsHt)} HT (Stripe + site)${summaryNow.mc2WithSubscriptions.subscriptionDeductions.map((d) => ` − ${d.label} sur les abonnements ${money(d.amount)}`).join('')}`
              : 'voir la tuile MC2 %'
          }
        />
      </div>

      <h2 className="section-heading">
        Publicité
        <span className="section-heading__sub">ROAS par régie : « régie » = achats que la plateforme s'attribue × prix HT d'un kit ; « réel » = kits arrivés par un clic pub balisé, vus par la boutique</span>
      </h2>
      <div className="kpi-row">
        <StatTile
          label="ROAS Meta (HT)"
          value={summaryNow?.roasMeta != null ? formatRatio(summaryNow.roasMeta) : '—'}
          deltas={summaryNow?.roasMeta != null ? summaryDeltas((m) => m.roasMeta, formatRatio, true) : undefined}
          hint={
            summaryNow?.roasMeta != null && summaryNow.roasKitPriceHt != null && summaryNow.metaSpend != null
              ? `${formatInteger(Math.round(summaryNow.roasMetaKits))} achats attribués par Meta × ${money(summaryNow.roasKitPriceHt)} (prix HT moyen d'un Piano LED sur la période) ÷ ${money(summaryNow.metaSpend)} de pub Meta`
              : summaryNow?.roasKitPriceHt == null
                ? 'aucun Piano LED vendu sur la période : pas de prix de référence'
                : 'en attente de Meta'
          }
        />
        <StatTile
          label="ROAS Meta réel (HT)"
          value={summaryNow?.roasMetaReal != null ? formatRatio(summaryNow.roasMetaReal) : '—'}
          deltas={summaryNow?.roasMetaReal != null ? summaryDeltas((m) => m.roasMetaReal, formatRatio, true) : undefined}
          hint={
            summaryNow?.metaSpend != null
              ? `${formatInteger(summaryNow.roasMetaRealKits)} Piano LED arrivés par un clic pub Meta (UTM de la dernière visite), ${money(summaryNow.roasMetaRealHt)} HT ÷ ${money(summaryNow.metaSpend)} de pub Meta — ce que la boutique a vu, borne basse : un clic sans UTM ne compte pas`
              : 'en attente de Meta'
          }
        />
        <StatTile
          label="ROAS Google Ads (HT)"
          value={summaryNow?.roasGoogle != null ? formatRatio(summaryNow.roasGoogle) : '—'}
          deltas={summaryNow?.roasGoogle != null ? summaryDeltas((m) => m.roasGoogle, formatRatio, true) : undefined}
          hint={
            summaryNow?.roasGoogle != null && summaryNow.roasKitPriceHt != null && summaryNow.googleSpend != null
              ? `${formatInteger(Math.round(summaryNow.roasGoogleKits))} conversions attribuées par Google Ads × ${money(summaryNow.roasKitPriceHt)} (prix HT moyen d'un Piano LED sur la période) ÷ ${money(summaryNow.googleSpend)} de pub Google`
              : !googleAdsLive
                ? 'Google Ads non branché'
                : summaryNow?.roasKitPriceHt == null
                  ? 'aucun Piano LED vendu sur la période : pas de prix de référence'
                  : 'aucune dépense Google Ads sur la période'
          }
        />
        <StatTile
          label="ROAS Google Ads réel (HT)"
          value={summaryNow?.roasGoogleReal != null ? formatRatio(summaryNow.roasGoogleReal) : '—'}
          deltas={summaryNow?.roasGoogleReal != null ? summaryDeltas((m) => m.roasGoogleReal, formatRatio, true) : undefined}
          hint={
            summaryNow?.googleSpend != null
              ? `${formatInteger(summaryNow.roasGoogleRealKits)} Piano LED arrivés par un clic pub Google (UTM cpc / product_sync de la dernière visite), ${money(summaryNow.roasGoogleRealHt)} HT ÷ ${money(summaryNow.googleSpend)} de pub Google — borne basse : Shopify ne garde pas le gclid, seuls les clics balisés en UTM comptent`
              : !googleAdsLive
                ? 'Google Ads non branché'
                : 'aucune dépense Google Ads sur la période'
          }
        />
        <StatTile
          label="ROAS payback (HT)"
          value={summaryNow?.roasPayback != null ? formatRatio(summaryNow.roasPayback) : '—'}
          deltas={summaryNow?.roasPayback != null ? summaryDeltas((m) => m.roasPayback, formatRatio, true) : undefined}
          hint={
            subsTotals && summaryNow?.roasNewSubscribersMrrHt != null
              ? `HT — (Piano LED + ${money(summaryNow.roasNewSubscribersMrrHt)} : ${formatInteger(subsTotals.newSubscribers)} nouveaux abonnés × un mois, 8,25 € ou 9,90 €, 5,83 € pour un abonnement du site, ÷ 1,20) ÷ pub`
              : 'en attente des abonnements'
          }
        />
      </div>

      <div className="chart-grid">
        <SalesTrendChart
          className="chart-grid__wide"
          title="Chiffre d'affaires total (TTC)"
          subtitle={amazonLive ? 'Shopify + Amazon + abonnements, TTC' : 'Boutique + abonnements, remboursements déduits, TTC'}
          current={totalCurrent}
          reference={totalReference}
          referenceLabel={referenceLabel}
          value={totalRevenueOf}
          formatValue={money}
          formatAxis={moneyAxis}
        />
      </div>

      <h2 className="section-heading">Distribution du revenu</h2>
      <div className="chart-grid">
        <DonutChart
          title="Par canal (TTC)"
          subtitle="CA net TTC de la période, remboursements déduits"
          slices={channelSlices}
          formatValue={money}
          emptyMessage={subs ? 'Aucune vente sur cette période.' : 'En attente des abonnements.'}
        />
        <DonutChart
          title="Répartition du CA HT"
          subtitle="Piano LED (Shopify + Amazon + BtoB), pianos, abonnements (Stripe + site), autres (accessoires, port)"
          slices={
            summaryNow?.totalHt != null
              ? [
                  { key: 'kit', label: 'Piano LED', value: summaryNow.kits.ht, color: CATEGORICAL_COLORS[0] },
                  { key: 'piano', label: 'Pianos', value: summaryNow.pianos.ht, color: CATEGORICAL_COLORS[2] },
                  { key: 'subscription', label: 'Abonnements', value: summaryNow.subscriptions?.ht ?? 0, color: CATEGORICAL_COLORS[1] },
                  { key: 'other', label: 'Autres', value: Math.max(summaryNow.others.ht, 0), color: OTHER_COLOR },
                ].filter((slice) => slice.value > 0)
              : []
          }
          formatValue={money}
          emptyMessage="En attente des ventes et des abonnements."
        />
        <DonutChart
          title="Marge nette par canal (HT)"
          subtitle="HT, après pub — Shopify : CA − Cogs − frais Shopify Payments − envoi − pub Meta et Google · Abonnements : encaissé sur la période (Stripe + site − remboursements) HT − retenues · Amazon et BtoB : CA − coût de revient des kits (− pub Amazon)"
          slices={netMarginSlices}
          formatValue={money}
        />
        <DonutChart
          title="Par pays (TTC)"
          subtitle={`CA produits TTC${amazonLive ? ' (Shopify + Amazon)' : ''}, par pays de livraison — dix premiers pays et les autres`}
          slices={countrySlices}
          formatValue={money}
        />
      </div>

      <div className="chart-grid">
        <PlatformRevenueChart dates={(data?.daily.current ?? []).map((day) => day.date)} series={platformRevenueSeries} currency={currency} />
      </div>

      <div className="chart-grid">
        <AdsPlatformChart
          title="Ads metrics over time"
          metaLabel="Facebook"
          withBlendedCpa
          period={dataPeriod(period, 'live')}
          withMeta
          withAmazon={amazonAdsLive}
          currency={currency}
        />
      </div>

      {!ga4Ready && (
        <p className="notice notice--warning" role="note">
          Google Analytics n'a pas encore été synchronisé : sessions et CR% viennent en attendant des sessions{' '}
          <strong>Shopify</strong>.
        </p>
      )}
      <div className="chart-grid">
        <SessionsCrChart daily={ga4SalesDaily} source={ga4Ready ? 'ga4' : 'shopify'} />
      </div>
      <h2 className="section-heading">
        Marges
        <span className="section-heading__sub">HT — sans puis avec abonnements, puis branding et charges fixes</span>
      </h2>
      {/* Un encadré par niveau de marge : le montant et son taux côte à côte,
          la formule en titre — on voit d'un coup d'œil ce que chaque MC retire. */}
      <div className="kpi-groups">
        <section className="kpi-group">
          <h3 className="kpi-group__title">
            MC2 sans abonnements
            <span className="kpi-group__formula">Piano LED + pianos HT − coût de revient − frais Shopify Payments − pub</span>
          </h3>
          <div className="kpi-group__tiles">
        <StatTile
          label="MC2 sans abo (HT)"
          value={summaryNow?.mc2 ? money(summaryNow.mc2.margin) : '—'}
          deltas={summaryNow?.mc2 ? summaryDeltas((m) => m.mc2?.margin ?? null, money, true) : undefined}
          hint={
            summaryNow?.mc2
              ? `Piano LED + pianos ${money(summaryNow.mc2.revenueHt)} HT − coût de revient ${money(summaryNow.mc2.cogs)} − frais Shopify Payments ${money(summaryNow.mc2.paymentFees)} − pub ${money(summaryNow.mc2.adSpend)}`
              : 'voir la tuile MC2 %'
          }
        />
        <StatTile
          label="MC2 % sans abo (HT)"
          value={summaryNow?.mc2?.percent != null ? formatPercent(summaryNow.mc2.percent) : '—'}
          deltas={summaryNow?.mc2?.percent != null ? summaryDeltas((m) => m.mc2?.percent ?? null, formatPercent, true) : undefined}
          hint={
            summaryNow?.mc2
              ? `${money(summaryNow.mc2.margin)} = Piano LED + pianos ${money(summaryNow.mc2.revenueHt)} HT − coût de revient ${money(summaryNow.mc2.cogs)} − frais Shopify Payments ${money(summaryNow.mc2.paymentFees)} − pub ${money(summaryNow.mc2.adSpend)}`
              : (summaryNow?.kitsWithoutCost ?? 0) > 0
                ? `${formatInteger(summaryNow?.kitsWithoutCost ?? 0)} kits sans coût de revient : à saisir sur la page Frais`
                : (summaryNow?.pianosWithoutCost ?? 0) > 0
                  ? `${formatInteger(summaryNow?.pianosWithoutCost ?? 0)} pianos sans coût : à saisir sur la page Frais`
                  : !shopPaymentFee
                  ? 'frais Shopify Payments manquants (page Frais)'
                  : 'en attente de la pub'
          }
        />
          </div>
        </section>
        <section className="kpi-group">
          <h3 className="kpi-group__title">
            MC2 avec abonnements
            <span className="kpi-group__formula">MC2 + abonnements encaissés HT − retenues (Sacem, fees Stripe…)</span>
          </h3>
          <div className="kpi-group__tiles">
        <StatTile
          label="MC2 avec abo (HT)"
          value={summaryNow?.mc2WithSubscriptions ? money(summaryNow.mc2WithSubscriptions.margin) : '—'}
          deltas={summaryNow?.mc2WithSubscriptions ? summaryDeltas((m) => m.mc2WithSubscriptions?.margin ?? null, money, true) : undefined}
          hint={
            summaryNow?.mc2WithSubscriptions
              ? `MC2 sans abo + abonnements encaissés ${money(summaryNow.mc2WithSubscriptions.subscriptionsHt)} HT (Stripe + site)${summaryNow.mc2WithSubscriptions.subscriptionDeductions.map((d) => ` − ${d.label} sur les abonnements ${money(d.amount)}`).join('')}`
              : 'voir la tuile MC2 %'
          }
        />
        <StatTile
          label="MC2 % avec abo (HT)"
          value={summaryNow?.mc2WithSubscriptions?.percent != null ? formatPercent(summaryNow.mc2WithSubscriptions.percent) : '—'}
          deltas={
            summaryNow?.mc2WithSubscriptions?.percent != null
              ? summaryDeltas((m) => m.mc2WithSubscriptions?.percent ?? null, formatPercent, true)
              : undefined
          }
          hint={
            summaryNow?.mc2WithSubscriptions
              ? `${money(summaryNow.mc2WithSubscriptions.margin)} = MC2 sans abo + abonnements ${money(summaryNow.mc2WithSubscriptions.subscriptionsHt)} HT${summaryNow.mc2WithSubscriptions.subscriptionDeductions.map((d) => ` − ${d.label} sur les abonnements ${money(d.amount)}`).join('')}, ÷ CA HT ${money(summaryNow.mc2WithSubscriptions.revenueHt)} abonnements compris`
              : summaryNow?.mc2
                ? 'en attente des abonnements'
                : 'comme la MC2 sans abo'
          }
        />
          </div>
        </section>
        <section className="kpi-group">
          <h3 className="kpi-group__title">
            MC3
            <span className="kpi-group__formula">MC2 avec abonnements − branding</span>
          </h3>
          <div className="kpi-group__tiles">
        <StatTile
          label="MC3 (HT)"
          value={summaryNow?.mc3 ? money(summaryNow.mc3.margin) : '—'}
          deltas={summaryNow?.mc3 ? summaryDeltas((m) => m.mc3?.margin ?? null, money, true) : undefined}
          hint={
            summaryNow?.mc3
              ? `MC2 avec abo ${money(summaryNow.mc2WithSubscriptions?.margin ?? 0)} − branding ${money(summaryNow.mc3.branding)} (page Frais, lissé sur la période)`
              : summaryNow?.mc2WithSubscriptions
                ? 'en attente du branding'
                : 'voir la tuile MC2 %'
          }
        />
        <StatTile
          label="MC3 % (HT)"
          value={summaryNow?.mc3?.percent != null ? formatPercent(summaryNow.mc3.percent) : '—'}
          deltas={summaryNow?.mc3?.percent != null ? summaryDeltas((m) => m.mc3?.percent ?? null, formatPercent, true) : undefined}
          hint={summaryNow?.mc3 ? `MC3 ÷ CA HT ${money(summaryNow.mc2WithSubscriptions?.revenueHt ?? 0)} (kits + pianos + abonnements)` : 'voir la tuile MC3'}
        />
          </div>
        </section>
        <section className="kpi-group">
          <h3 className="kpi-group__title">
            MC4
            <span className="kpi-group__formula">MC3 − charges fixes</span>
          </h3>
          <div className="kpi-group__tiles">
        <StatTile
          label="MC4 (HT)"
          value={summaryNow?.mc4 ? money(summaryNow.mc4.margin) : '—'}
          deltas={summaryNow?.mc4 ? summaryDeltas((m) => m.mc4?.margin ?? null, money, true) : undefined}
          hint={
            summaryNow?.mc4
              ? `MC3 ${money(summaryNow.mc3?.margin ?? 0)} − charges fixes ${money(summaryNow.mc4.fixedCharges)} (page Charges, au prorata des jours)`
              : summaryNow?.mc3
                ? 'en attente des charges'
                : 'voir la tuile MC3'
          }
        />
        <StatTile
          label="MC4 % (HT)"
          value={summaryNow?.mc4?.percent != null ? formatPercent(summaryNow.mc4.percent) : '—'}
          deltas={summaryNow?.mc4?.percent != null ? summaryDeltas((m) => m.mc4?.percent ?? null, formatPercent, true) : undefined}
          hint={summaryNow?.mc4 ? `MC4 ÷ CA HT ${money(summaryNow.mc2WithSubscriptions?.revenueHt ?? 0)} (kits + pianos + abonnements)` : 'voir la tuile MC4'}
        />
          </div>
        </section>
      </div>

      <h2 className="section-heading">
        Produits vendus
        <span className="section-heading__sub">retours déduits</span>
      </h2>
      <div className="kpi-row">
        <StatTile
          label="Piano LED"
          value={formatInteger(summaryNow?.kitsSold ?? 0)}
          deltas={summaryDeltas((m) => m.kitsSold, formatInteger)}
          hint={`kits, retours déduits — ${formatInteger(totals?.kits ?? 0)} Shopify${amazonLive ? ` + ${formatInteger(amazonTotals?.kits ?? 0)} Amazon` : ''}${btobTotals?.units ? ` + ${formatInteger(btobTotals.units)} BtoB` : ''}`}
        />
        <StatTile
          label="Pianos"
          value={formatInteger(summaryNow?.pianosSold ?? 0)}
          deltas={summaryDeltas((m) => m.pianosSold, formatInteger)}
          hint="Donner et « Piano compatible », Shopify, retours déduits"
        />
        <StatTile
          label="Abonnements vendus sur le site"
          value={formatInteger(summaryNow?.siteSubscriptionsSold ?? 0)}
          deltas={summaryDeltas((m) => m.siteSubscriptionsSold, formatInteger)}
          hint="« Abonnement 1 an » sur Shopify, offerts compris"
        />
      </div>

      {/* Résultat : la marge nette des canaux, moins les charges fixes. */}
      <h2 className="section-heading">
        Résultat
        <span className="section-heading__sub">HT, après pub et charges fixes</span>
      </h2>
      <div className="kpi-row">
        <StatTile
          label="Marge nette des canaux (HT)"
          value={money(netMarginTotal)}
          hint={
            netMarginIncomplete.length > 0
              ? `sans ${netMarginIncomplete.join(', ')} (réglage manquant ou pas encore branché)`
              : 'somme du camembert « Marge nette par canal »'
          }
        />
        <StatTile
          label="Charges fixes (HT)"
          value={chargesStats ? money(chargesStats.comparison.current.totals.total) : '—'}
          deltas={
            chargesStats
              ? summaryRefs.map(({ label, key }) =>
                  volumeDelta(
                    { label, available: chargesStats.comparison[key].totals.total > 0 },
                    chargesStats.comparison.current.totals.total,
                    chargesStats.comparison[key].totals.total,
                    money,
                    null,
                    'aucune charge',
                  ),
                )
              : undefined
          }
          hint={chargesStats && chargesStats.charges.length === 0 ? 'aucune charge saisie — page Charges' : 'salaires, loyer, logiciels… au prorata de la période'}
        />
        <StatTile
          label="Résultat net (HT)"
          value={chargesStats ? money(netMarginTotal - chargesStats.comparison.current.totals.total) : '—'}
          hint="marge nette des canaux − charges fixes"
        />
      </div>

      {/* Un chiffre clé par source : le détail est dans chaque page. */}
      <h2 className="section-heading">En bref</h2>
      <div className="kpi-row">
        <StatTile
          label="Commandes"
          value={formatInteger(totals?.orders ?? 0)}
          deltas={deltas((t) => t.orders, formatInteger)}
          hint="boutique, hors annulations"
        />
        <StatTile
          label="Abonnés actifs"
          value={subsTotals ? formatInteger(subsTotals.activeAtEnd) : '—'}
          deltas={subsTotals ? subsVolume((t) => t.activeAtEnd, formatInteger) : undefined}
          hint={subs ? `au dernier jour — en direct : ${formatInteger(subs.live.activeSubscribers)}` : 'en attente de Stripe'}
        />
        <StatTile
          label="MRR (TTC)"
          value={subsTotals ? money(subsTotals.mrrAtEnd) : '—'}
          deltas={subsTotals ? subsVolume((t) => t.mrrAtEnd, money) : undefined}
          hint={
            subs && subsTotals
              ? `au dernier jour : Stripe ${money(subsTotals.mrrStripeAtEnd)} + site ${money(subsTotals.mrrSiteAtEnd)} (${formatInteger(subsTotals.mrrSiteSubscribersAtEnd)} abonnement${subsTotals.mrrSiteSubscribersAtEnd > 1 ? 's' : ''} vendu${subsTotals.mrrSiteSubscribersAtEnd > 1 ? 's' : ''} sur le site, activé${subsTotals.mrrSiteSubscribersAtEnd > 1 ? 's' : ''} dans Stripe, au prix du site ÷ 12 et sortis du MRR Stripe) — en direct : ${money(subs.live.mrr)}`
              : 'en attente de Stripe'
          }
        />
        <StatTile
          label="Dépenses pub (HT)"
          value={summaryNow?.spendTotal != null ? money(summaryNow.spendTotal) : '—'}
          deltas={summaryNow?.spendTotal != null ? summaryRefs.map(({ label, view: ref }) =>
            volumeDelta({ label, available: ref?.spendTotal != null && ref.spendTotal > 0 }, summaryNow.spendTotal, ref?.spendTotal ?? null, money, null, 'aucune pub'),
          ) : undefined}
          hint={
            ads
              ? `Meta ${money(adsTotals?.spend ?? 0)}${amazonAdsLive ? ` + Amazon ${money(amazonAdsTotals?.spend ?? 0)}` : ''}${googleAdsLive ? ` + Google ${money(googleAdsTotals?.spend ?? 0)}` : ''}, en euros`
              : adsQuery.error ? 'indisponible' : 'en attente de Meta'
          }
        />
        <StatTile
          label="CA produits TTC − pub HT"
          value={summaryNow?.productsAfterAds != null ? money(summaryNow.productsAfterAds) : '—'}
          deltas={summaryNow?.productsAfterAds != null ? summaryDeltas((m) => m.productsAfterAds, money, true) : undefined}
          hint="CA produits − dépenses pub"
        />
      </div>

      {/* L'entonnoir CA HT → MC4 ferme la page : c'est la synthèse de tout ce qui précède. */}
      <div className="chart-grid">
        <FinancialFunnel steps={summaryNow?.funnel ?? null} currency={currency} />
      </div>

      </>
      )}

      {view === 'shopify' && (
      <>
      <h2 className="section-heading">
        Ventes
        <span className="section-heading__sub">hors annulations et commandes de test, remboursements déduits</span>
      </h2>
      <div className="kpi-row kpi-row--6">
        <StatTile
          label="CA TTC"
          value={money(totals?.netRevenue ?? 0)}
          deltas={deltas((t) => t.netRevenue, money)}
          hint="encaissé − remboursé, port compris"
        />
        <StatTile
          label="CA HT"
          value={money(totals?.netRevenueExclTax ?? 0)}
          deltas={deltas((t) => t.netRevenueExclTax, money)}
          hint="hors TVA réelle des commandes"
        />
        <StatTile
          label="Commandes"
          value={formatInteger(totals?.orders ?? 0)}
          deltas={deltas((t) => t.orders, formatInteger)}
          hint="hors annulations et commandes de test"
        />
        <StatTile
          label="Panier moyen (TTC)"
          value={totals?.averageOrderValue != null ? formatMoney(totals.averageOrderValue, currency, true) : '—'}
          deltas={deltas((t) => t.averageOrderValue, (v) => formatMoney(v, currency, true))}
          hint="montant payé par commande, avant remboursements"
        />
        <StatTile
          label="Produits vendus"
          value={formatInteger(totals?.units ?? 0)}
          deltas={deltas((t) => t.units, formatInteger)}
          hint="unités commandées, toutes lignes confondues"
        />
        <StatTile
          label="CR%"
          value={totals?.conversionRatePercent != null ? formatPercent(totals.conversionRatePercent) : '—'}
          deltas={
            totals?.conversionRatePercent != null
              ? (overlap
                  ? [{ label: "l'an dernier", window: comparison?.yearAgo }]
                  : [
                      { label: 'période préc.', window: comparison?.previous },
                      { label: "l'an dernier", window: comparison?.yearAgo },
                    ]
                ).map(({ label, window }) =>
                  rateDelta(
                    { label, available: window?.totals.conversionRatePercent != null },
                    totals.conversionRatePercent,
                    window?.totals.conversionRatePercent ?? null,
                    true,
                    'pas de sessions',
                  ),
                )
              : undefined
          }
          hint={
            totals?.sessions != null
              ? `${formatInteger(totals.orders)} commandes ÷ ${formatInteger(totals.sessions)} sessions`
              : 'sessions Shopify pas encore lues'
          }
        />
      </div>
      <div className="kpi-row kpi-row--4">
        <StatTile
          label="Remboursements (TTC)"
          value={money(totals?.refunds ?? 0)}
          deltas={deltas((t) => t.refunds, money, false)}
          hint={REFUND_BASIS_LABELS[refundBasis]}
        />
        <StatTile
          label="Taux de retour"
          value={totals && returnRate(totals) !== null ? formatPercent(returnRate(totals) ?? 0) : '—'}
          deltas={(overlap
            ? [{ label: "l'an dernier", window: comparison?.yearAgo }]
            : [
                { label: 'période préc.', window: comparison?.previous },
                { label: "l'an dernier", window: comparison?.yearAgo },
              ]
          ).map(({ label, window }) =>
            rateDelta({ label, available: (window?.totals.orders ?? 0) > 0 }, totals ? returnRate(totals) : null, window ? returnRate(window.totals) : null, false, 'aucune vente'),
          )}
          hint="remboursements ÷ CA encaissé avant remboursements"
        />
        <StatTile
          label="Remises accordées (TTC)"
          value={money(totals?.discounts ?? 0)}
          deltas={deltas((t) => t.discounts, money, false)}
          hint={
            totals && totals.grossRevenue > 0
              ? `${formatPercent((totals.discounts / (totals.grossRevenue + totals.discounts)) * 100)} du montant avant remises`
              : 'codes promo et remises automatiques'
          }
        />
        <StatTile
          label={today ? "Aujourd'hui, en cours (TTC)" : "Aujourd'hui (TTC)"}
          value={today ? money(today.current.grossRevenue) : '—'}
          deltas={todayDeltas}
          hint={
            today
              ? `${formatInteger(today.current.orders)} commande${today.current.orders > 1 ? 's' : ''}, jusqu'au ${formatInstant(today.asOf)} — comparé à la même heure`
              : 'aucun sync abouti'
          }
        />
      </div>

      <h2 className="section-heading">
        Marges
        <span className="section-heading__sub">HT — MC1 = CA HT − Cogs − frais de paiement ; MC1' = MC1 − envoi ; MC2 = MC1 − pub ; MC3 = MC2 − branding</span>
      </h2>
      <div className="kpi-row kpi-row--4">
        <StatTile
          label="Cogs (HT)"
          value={money(totals?.cogs ?? 0)}
          deltas={deltas((t) => t.cogs, money, null)}
          hint={
            totals && totals.cogsMissingUnits > 0
              ? `coût Shopify × unités gardées — ${formatInteger(totals.cogsMissingUnits)} unité${totals.cogsMissingUnits > 1 ? 's' : ''} sans coût dans Shopify, non comptée${totals.cogsMissingUnits > 1 ? 's' : ''}`
              : 'coût par article Shopify × unités gardées (retours déduits)'
          }
        />
        <StatTile
          label="Frais d'envoi (HT)"
          value={shippingCostPerOrder !== null ? money((totals?.orders ?? 0) * shippingCostPerOrder) : '—'}
          deltas={shippingCostPerOrder !== null ? deltas((t) => t.orders * shippingCostPerOrder, money, null) : undefined}
          hint={
            shippingCostPerOrder !== null
              ? `${formatInteger(totals?.orders ?? 0)} commandes × ${formatMoney(shippingCostPerOrder, currency, true)}`
              : 'renseigner le coût d\'envoi par commande sur le Résumé'
          }
        />
        <StatTile
          label="MC1 (HT)"
          value={mcNow?.mc1 != null ? money(mcNow.mc1) : '—'}
          deltas={mcVolume((m) => m.mc1)}
          hint={
            shopPaymentFee === null
              ? 'renseigner les frais Shopify Payments sur la page Frais'
              : `CA HT − Cogs − frais Shopify Payments (${formatDecimal(shopPaymentFee.percent)} % du TTC encaissé + ${formatMoney(shopPaymentFee.perOrder, currency, true)} par commande, page Frais)`
          }
        />
        <StatTile
          label="MC1 %"
          value={mcNow?.mc1Percent != null ? formatPercent(mcNow.mc1Percent) : '—'}
          deltas={mcRate((m) => m.mc1Percent)}
          hint="MC1 ÷ CA HT"
        />
      </div>
      <div className="kpi-row kpi-row--4">
        <StatTile
          label="MC1' (HT)"
          value={mcNow?.mc1Prime != null ? money(mcNow.mc1Prime) : '—'}
          deltas={mcVolume((m) => m.mc1Prime)}
          hint={shippingCostPerOrder === null ? 'renseigner le coût d\'envoi sur la page Frais' : 'MC1 − frais d\'envoi'}
        />
        <StatTile
          label="MC1' %"
          value={mcNow?.mc1PrimePercent != null ? formatPercent(mcNow.mc1PrimePercent) : '—'}
          deltas={mcRate((m) => m.mc1PrimePercent)}
          hint="MC1' ÷ CA HT"
        />
        <StatTile
          label="MC2 (HT)"
          value={mcNow?.mc2 != null ? money(mcNow.mc2) : '—'}
          deltas={mcVolume((m) => m.mc2)}
          hint={mcNow ? `MC1 − Ads (${money(mcNow.ads)} Meta)` : 'MC1 − Ads'}
        />
        <StatTile
          label="MC3 (HT)"
          value={mcNow?.mc3 != null ? money(mcNow.mc3) : '—'}
          deltas={mcVolume((m) => m.mc3)}
          hint={
            brandingStats
              ? `MC2 − branding ${money(brandingStats.comparison.current.total)} (page Frais, lissé sur la période)`
              : 'en attente du branding'
          }
        />
      </div>

      <div className="chart-grid">
        <SalesTrendChart
          title="CA boutique (TTC)"
          subtitle="Encaissé moins remboursé, TTC, en heure de Paris"
          current={data?.daily.current ?? []}
          reference={reference ?? []}
          referenceLabel={referenceLabel}
          value={netRevenueOf}
          formatValue={money}
          formatAxis={moneyAxis}
        />
        <SalesTrendChart
          title="Commandes"
          subtitle="Hors annulations et commandes de test"
          current={data?.daily.current ?? []}
          reference={reference ?? []}
          referenceLabel={referenceLabel}
          value={ordersOf}
          formatValue={formatInteger}
          formatAxis={formatCompact}
        />

        <TopProductsTable
          rows={data?.topProducts ?? []}
          currency={currency}
          comparisonsOverlap={overlap}
        />
      </div>
      <div className="chart-grid">
        <ShopPerformanceTable
          title="Par jour"
          subtitle="Chaque jour de la période, le plus récent en haut — 7 jours visibles, le reste défile"
          firstHeader="Date"
          rows={[...(data?.daily.current ?? [])].reverse().map((day) => ({ key: day.date, label: formatDayLong(day.date), figures: day }))}
          total={{ label: 'Total', figures: sumShopFigures(data?.daily.current ?? []) }}
          visibleRows={7}
          paymentFee={shopPaymentFee}
          shippingCostPerOrder={shippingCostPerOrder}
          currency={currency}
        />
      </div>

      <div className="chart-grid">
        <SalesTrendChart
          className="chart-grid__wide"
          title="CA HT par jour"
          subtitle="CA HT de la boutique, chaque jour de la période"
          current={data?.daily.current ?? []}
          reference={[]}
          referenceLabel=""
          currentLabel="CA HT"
          dots
          value={shopRevenueExclTaxOf}
          formatValue={money}
          formatAxis={moneyAxis}
        />
      </div>

      <div className="chart-grid">
        <ShopPerformanceTable
          title="Par pays"
          subtitle={`Les ${TOP_COUNTRIES} pays au plus gros CA HT sur la période, puis l'ensemble de la boutique`}
          firstHeader="Country"
          rows={topShopCountries.map((row) => ({ key: row.country ?? 'none', label: countryName(row.country), figures: row }))}
          total={{ label: 'Global', figures: sumShopFigures(data?.countryPerformance ?? []) }}
          paymentFee={shopPaymentFee}
          shippingCostPerOrder={shippingCostPerOrder}
          currency={currency}
        />
      </div>

      <div className="chart-grid">
        <RankedTrendChart
          title="CA HT par jour et par pays"
          noun="pays"
          dates={shopDates}
          keys={topShopCountryCodes}
          daily={countryDailyPoints}
          nameOf={countryName}
          currency={currency}
        />
      </div>

      <h2 className="section-heading">
        Répartition par pays
        <span className="section-heading__sub">{TOP_COUNTRIES} premiers pays au CA HT, puis les autres</span>
      </h2>
      <div className="chart-grid chart-grid--thirds">
        <DonutChart
          title="CA HT"
          subtitle="Part de chaque pays dans le CA HT de la boutique"
          slices={countryShareSlices((row) => row.netRevenueExclTax)}
          formatValue={money}
        />
        <DonutChart
          title="MC1 (HT)"
          subtitle="Part de chaque pays dans le MC1 (CA HT − Cogs − frais de paiement)"
          slices={shopPaymentFee ? countryShareSlices(rowMc1) : []}
          formatValue={money}
          emptyMessage="Ajouter une retenue « Fees Stripe » sur la page Souscriptions."
        />
        <CountryAovChart
          bars={topShopCountries
            .filter((row) => row.orders > 0)
            .map((row, index) => ({
              key: row.country ?? 'none',
              label: countryName(row.country),
              orders: row.orders,
              aov: Math.round((row.grossRevenue / row.orders) * 100) / 100,
              color: CATEGORICAL_COLORS[index] ?? OTHER_COLOR,
            }))}
          global={
            totals && totals.orders > 0
              ? { aov: Math.round((totals.grossRevenue / totals.orders) * 100) / 100, orders: totals.orders }
              : null
          }
          currency={currency}
        />
      </div>

      <div className="chart-grid">
        <ShopPerformanceTable
          title="Par site"
          subtitle="Site d'où venait le client lors de la visite qui a mené à la commande — « Inconnu » : parcours non suivi par Shopify (cookies refusés…)"
          firstHeader="Site"
          rows={shopSites.map((row) => ({ key: row.site ?? 'none', label: siteName(row.site), figures: row }))}
          total={{ label: 'Total général', figures: sumShopFigures(data?.sitePerformance ?? []) }}
          visibleRows={10}
          paymentFee={shopPaymentFee}
          shippingCostPerOrder={shippingCostPerOrder}
          currency={currency}
        />
      </div>

      <div className="chart-grid">
        <RankedTrendChart
          title="CA HT par jour et par site"
          noun="sites"
          dates={shopDates}
          keys={topShopSiteKeys}
          daily={siteDailyPoints}
          nameOf={siteName}
          currency={currency}
        />
      </div>

      <h2 className="section-heading">
        Répartition par site
        <span className="section-heading__sub">{TOP_COUNTRIES} premiers sites au CA HT, puis les autres</span>
      </h2>
      <div className="chart-grid chart-grid--thirds">
        <DonutChart
          title="CA HT"
          subtitle="Part de chaque site dans le CA HT de la boutique"
          slices={siteShareSlices((row) => row.netRevenueExclTax)}
          formatValue={money}
        />
        <DonutChart
          title="MC1 (HT)"
          subtitle="Part de chaque site dans le MC1 (CA HT − Cogs − frais de paiement)"
          slices={shopPaymentFee ? siteShareSlices(rowMc1) : []}
          formatValue={money}
          emptyMessage="Ajouter une retenue « Fees Stripe » sur la page Souscriptions."
        />
        <CountryAovChart
          noun="site"
          bars={shopSites
            .slice(0, TOP_COUNTRIES)
            .filter((row) => row.orders > 0)
            .map((row, index) => ({
              key: row.site ?? 'none',
              label: siteName(row.site),
              orders: row.orders,
              aov: Math.round((row.grossRevenue / row.orders) * 100) / 100,
              color: CATEGORICAL_COLORS[index] ?? OTHER_COLOR,
            }))}
          global={
            totals && totals.orders > 0
              ? { aov: Math.round((totals.grossRevenue / totals.orders) * 100) / 100, orders: totals.orders }
              : null
          }
          currency={currency}
        />
      </div>

      <div className="chart-grid">
        <ShopPerformanceTable
          title="Par produit"
          subtitle="Montants de chaque commande (payé, port, remboursements, envoi) répartis entre ses produits au prorata de leur CA — AOV : CA du produit ÷ commandes qui le contiennent"
          firstHeader="Product"
          rows={shopProducts.map((row) => ({
            key: row.productKey,
            label: row.title,
            figures: { ...row, aovOrders: row.ordersWithProduct },
          }))}
          total={{ label: 'Total général', figures: totals ?? sumShopFigures(shopProducts) }}
          visibleRows={10}
          paymentFee={shopPaymentFee}
          shippingCostPerOrder={shippingCostPerOrder}
          currency={currency}
        />
      </div>

      <div className="chart-grid">
        <RankedTrendChart
          title="CA HT par jour et par produit"
          noun="produits"
          dates={shopDates}
          keys={topShopProductKeys}
          daily={productDailyPoints}
          nameOf={productTitle}
          currency={currency}
        />
      </div>

      <h2 className="section-heading">
        Répartition par produit
        <span className="section-heading__sub">{TOP_COUNTRIES} premiers produits au CA HT, puis les autres</span>
      </h2>
      <div className="chart-grid chart-grid--thirds">
        <DonutChart
          title="CA HT"
          subtitle="Part de chaque produit dans le CA HT de la boutique"
          slices={productShareSlices((row) => row.netRevenueExclTax)}
          formatValue={money}
        />
        <DonutChart
          title="MC1 (HT)"
          subtitle="Part de chaque produit dans le MC1 (CA HT − Cogs − frais de paiement)"
          slices={shopPaymentFee ? productShareSlices(rowMc1) : []}
          formatValue={money}
          emptyMessage="Ajouter une retenue « Fees Stripe » sur la page Souscriptions."
        />
        <CountryAovChart
          noun="produit (commandes qui le contiennent)"
          labelWidth={180}
          bars={shopProducts
            .slice(0, TOP_COUNTRIES)
            .filter((row) => row.ordersWithProduct > 0)
            .map((row, index) => ({
              key: row.productKey,
              label: row.title,
              orders: row.ordersWithProduct,
              aov: Math.round((row.grossRevenue / row.ordersWithProduct) * 100) / 100,
              color: CATEGORICAL_COLORS[index] ?? OTHER_COLOR,
            }))}
          global={
            totals && totals.orders > 0
              ? { aov: Math.round((totals.grossRevenue / totals.orders) * 100) / 100, orders: totals.orders }
              : null
          }
          currency={currency}
        />
      </div>
      </>
      )}

      {view === 'subscriptions' && (
      <>
      {subsMeta && !subsMeta.lastSyncAt && (
        <p className="notice">
          Aucune synchronisation Stripe n'a encore abouti. Vérifier que le conteneur{' '}
          <code>sync_stripe</code> tourne (<code>docker compose logs sync_stripe</code>).
        </p>
      )}
      {subsMeta?.lastSyncAt && !subsMeta.historyImported && (
        <p className="notice">Import de l'historique Stripe en cours : comparaisons incomplètes.</p>
      )}
      {subsMeta?.lastSyncFailed && (
        <p className="notice notice--error" role="alert">
          Le dernier passage du sync Stripe a échoué. Les chiffres sont ceux du{' '}
          {formatInstant(subsMeta.lastSyncAt)}.
        </p>
      )}

      {subs && (
        <div className={subscriptionsQuery.isFetching ? 'is-refetching' : undefined}>
          <h2 className="section-heading">Sales KPIs</h2>
          <div className="kpi-row">
            <StatTile
              label="CA TTC"
              value={money(subsTotals ? subsCaTtc(subsTotals) : 0)}
              deltas={subsVolume(subsCaTtc, money)}
              hint="MRR au dernier jour de la période, comme sur le Résumé"
            />
            <StatTile
              label="CA HT"
              value={money(subsTotals ? subsCaHt(subsTotals) : 0)}
              deltas={subsVolume(subsCaHt, money)}
              hint={`CA TTC ÷ ${formatDecimal(1 + SUBSCRIPTION_VAT_PERCENT / 100)} (TVA ${formatDecimal(SUBSCRIPTION_VAT_PERCENT)} %)`}
            />
            {deductionList.map((deduction) => (
              <StatTile
                key={deduction.id}
                label={deduction.label}
                value={money(subsTotals ? deductionAmount(deduction, subsTotals) : 0)}
                deltas={subsVolume((t) => deductionAmount(deduction, t), money, null)}
                hint={`${formatDecimal(deduction.percent)} % ${DEDUCTION_BASE_LABELS[deduction.base]}`}
              />
            ))}
            <StatTile
              label="Final HT"
              value={money(subsTotals ? finalExclTax(subsTotals) : 0)}
              deltas={subsVolume(finalExclTax, money)}
              hint={deductionList.length > 0 ? `CA HT − ${deductionList.map((d) => d.label).join(' − ')}` : 'CA HT, aucune retenue saisie'}
            />
          </div>
        </div>
      )}

      {subs && (
        <div className={subscriptionsQuery.isFetching ? 'is-refetching' : undefined}>
          <h2 className="section-heading">
            Subscriptions KPIs
            <span className="section-heading__sub">définitions du Dashboard Stripe</span>
          </h2>
          <div className="kpi-row kpi-row--4">
            <StatTile
              label="Souscriptions"
              value={formatInteger(subsTotals?.newSubscribers ?? 0)}
              deltas={subsVolume((t) => t.newSubscribers, formatInteger)}
              hint={`abonnements devenus payants sur la période (fins d'essai comprises) : ${formatInteger(subsTotals?.newSubscribersApp ?? 0)} via l'app · ${formatInteger(subsTotals?.newSubscribersSite ?? 0)} via le site (comptés à la fin de leur essai)`}
            />
            <StatTile
              label="Actives"
              value={formatInteger(subsTotals?.activeAtEnd ?? 0)}
              deltas={subsVolume((t) => t.activeAtEnd, formatInteger)}
              hint={`au dernier jour de la période — ${formatInteger(subsTotals?.annualAtEnd ?? 0)} annuels · ${formatInteger(subsTotals?.monthlyAtEnd ?? 0)} mensuels`}
            />
            <StatTile
              label="Actives à date"
              value={formatInteger(subs.live.activeSubscribers)}
              hint={`en direct, au dernier sync (${formatInstant(subs.live.asOf)}) — le chiffre du Dashboard Stripe`}
            />
            <StatTile
              label="Churn"
              value={subsTotals?.churnRatePercent != null ? formatPercent(subsTotals.churnRatePercent) : '—'}
              deltas={subsRate((t) => t.churnRatePercent, false)}
              hint={
                subsTotals
                  ? `${formatInteger(subsTotals.churned)} départ${subsTotals.churned > 1 ? 's' : ''} ÷ (${formatInteger(subsTotals.activeAtStart)} au début + ${formatInteger(subsTotals.newSubscribers)} nouveaux), comme Stripe`
                  : ''
              }
            />
          </div>
          <div className="kpi-row kpi-row--6">
            <StatTile
              label="Trials"
              value={formatInteger(subsTotals?.trialsStarted ?? 0)}
              deltas={subsVolume((t) => t.trialsStarted, formatInteger)}
              hint={`essais gratuits démarrés dans l'app — ${formatInteger(subsTotals?.trialsActiveAtEnd ?? 0)} en cours au dernier jour. À part, les abonnements vendus sur le site (déjà payés, « essai » d'un an chez Stripe) : ${formatInteger(subsTotals?.trialsStartedSite ?? 0)} démarrés, ${formatInteger(subsTotals?.trialsActiveAtEndSite ?? 0)} en cours`}
            />
            <StatTile
              label="Conversion des essais"
              value={subsTotals?.trialConversionPercent != null ? formatPercent(subsTotals.trialConversionPercent) : '—'}
              deltas={subsRate((t) => t.trialConversionPercent, true)}
              hint={
                subsTotals
                  ? `${formatInteger(subsTotals.trialsConverted)} essais devenus payants ÷ ${formatInteger(subsTotals.trialsEnded)} essais terminés sur la période, comme Stripe`
                  : ''
              }
            />
            <StatTile
              label="MRR TTC"
              value={money(subsTotals?.mrrAtEnd ?? 0)}
              deltas={subsVolume((t) => t.mrrAtEnd, money)}
              hint={`au dernier jour : Stripe ${money(subsTotals?.mrrStripeAtEnd ?? 0)} (prix TTC mensualisés des abonnés payants, moins réductions permanentes, sans les abonnements activés avec un code du site tant qu'ils n'ont pas payé leur deuxième année) + site ${money(subsTotals?.mrrSiteAtEnd ?? 0)} (${formatInteger(subsTotals?.mrrSiteSubscribersAtEnd ?? 0)} abonnement${(subsTotals?.mrrSiteSubscribersAtEnd ?? 0) > 1 ? 's' : ''} vendu${(subsTotals?.mrrSiteSubscribersAtEnd ?? 0) > 1 ? 's' : ''} sur le site et activé${(subsTotals?.mrrSiteSubscribersAtEnd ?? 0) > 1 ? 's' : ''} dans Stripe, chacun au prix moyen payé sur le site ÷ 12, soit 5,83 € pour 70 €, et non au tarif Stripe de 8,25 €) — en direct : Stripe ${money(subs.live.mrrStripe)} + site ${money(subs.live.mrrSite)} (${formatInteger(subs.live.siteSubscribers)})`}
            />
            <StatTile
              label="MRR Stripe (TTC)"
              value={money(subsTotals?.mrrStripeAtEnd ?? 0)}
              deltas={subsVolume((t) => t.mrrStripeAtEnd, money)}
              hint={`abonnés devenus payants chez Stripe (renouvellements compris), prix TTC mensualisés moins réductions permanentes — en direct : ${money(subs.live.mrrStripe)}`}
            />
            <StatTile
              label="MRR site (TTC)"
              value={money(subsTotals?.mrrSiteAtEnd ?? 0)}
              deltas={subsVolume((t) => t.mrrSiteAtEnd, money)}
              hint={`${formatInteger(subsTotals?.mrrSiteSubscribersAtEnd ?? 0)} abonnement${(subsTotals?.mrrSiteSubscribersAtEnd ?? 0) > 1 ? 's' : ''} vendu${(subsTotals?.mrrSiteSubscribersAtEnd ?? 0) > 1 ? 's' : ''} sur le site et activé${(subsTotals?.mrrSiteSubscribersAtEnd ?? 0) > 1 ? 's' : ''} dans Stripe (essai compris), chacun au prix moyen payé sur le site ÷ 12 (70 € → 5,83 €), sortis du MRR Stripe qui les compterait à 8,25 € ; ils y reviennent, à leur tarif Stripe, dès qu'ils paient leur deuxième année — en direct : ${money(subs.live.mrrSite)} (${formatInteger(subs.live.siteSubscribers)})`}
            />
            <StatTile
              label="ARR TTC"
              value={money((subsTotals?.mrrAtEnd ?? 0) * 12)}
              deltas={subsVolume((t) => t.mrrAtEnd * 12, money)}
              hint={`MRR TTC × 12 — en direct : ${money(subs.live.mrr * 12)}`}
            />
          </div>

          {/* D'où viennent les essais et les souscriptions : l'app (essai
              gratuit choisi par l'utilisateur) ou le site (abonnement 1 an
              acheté chez Shopify, activé chez Stripe par un « essai » d'un an). */}
          <div className="chart-grid">
            <DonutChart
              title="Essais démarrés : app / site"
              subtitle="Sur la période — l'essai « site » est l'année déjà payée d'un abonnement acheté sur le site, pas un essai gratuit"
              slices={[
                { key: 'app', label: "Via l'app", value: subsTotals?.trialsStarted ?? 0, color: CATEGORICAL_COLORS[1] },
                { key: 'site', label: 'Via le site', value: subsTotals?.trialsStartedSite ?? 0, color: CATEGORICAL_COLORS[4] },
              ]}
              formatValue={formatInteger}
              emptyMessage="Aucun essai sur cette période."
            />
            <DonutChart
              title="Souscriptions : app / site"
              subtitle="Abonnements devenus payants sur la période (essai terminé), selon leur origine — un abonnement acheté sur le site ne compte qu'à la fin de son essai"
              slices={[
                { key: 'app', label: "Via l'app", value: subsTotals?.newSubscribersApp ?? 0, color: CATEGORICAL_COLORS[1] },
                { key: 'site', label: 'Via le site', value: subsTotals?.newSubscribersSite ?? 0, color: CATEGORICAL_COLORS[4] },
              ]}
              formatValue={formatInteger}
              emptyMessage="Aucune souscription sur cette période."
            />
          </div>
        </div>
      )}

      {subs && (
        <div className={subscriptionsQuery.isFetching ? 'is-refetching' : undefined}>
          <h2 className="section-heading">
            Chiffre d'affaires encaissé
            <span className="section-heading__sub">factures Stripe payées + abonnements vendus sur le site − remboursements, à leur date</span>
          </h2>
          <div className="chart-grid">
            <SalesTrendChart
              className="chart-grid__wide"
              title="CA TTC encaissé par jour"
              subtitle="Comparé à la même période l'an dernier"
              current={subs.daily.current}
              reference={subs.daily.yearAgo}
              referenceLabel="Même période l'an dernier"
              currentLabel="CA TTC"
              dots
              value={subscriptionRevenueOf}
              formatValue={money}
              formatAxis={moneyAxis}
            />
            <SubscriptionWeeksTable
              daily={subs.daily.current}
              deductions={deductionList}
              vatPercent={SUBSCRIPTION_VAT_PERCENT}
              currency={currency}
            />
            <SubscriptionsChurnChart daily={subs.daily.current} />
          </div>

          {/* Trois stocks jour par jour, sur les jours de la période : le MRR,
              les abonnés payants et les essais en cours, chacun le soir. Stocks
              (`aggregate="last"`) : sur une tranche, c'est le dernier soir qui
              compte, pas la somme. */}
          <h2 className="section-heading">
            Parc jour par jour
            <span className="section-heading__sub">le soir de chaque jour de la période, comparé à la même période l'an dernier</span>
          </h2>
          <div className="chart-grid">
            <SalesTrendChart
              className="chart-grid__wide"
              title="MRR TTC par jour"
              subtitle="Prix mensualisés des abonnés payants moins réductions permanentes, abonnements du site à 1/12 de leur prix, le soir"
              current={subs.daily.current}
              reference={subs.daily.yearAgo}
              referenceLabel="Même période l'an dernier"
              currentLabel="MRR TTC"
              aggregate="last"
              dots
              value={(point) => point.mrr}
              formatValue={money}
              formatAxis={moneyAxis}
            />
            <SalesTrendChart
              title="Abonnés actifs par jour"
              subtitle="Abonnés payants le soir : sortis dès la demande de résiliation, essais et pauses exclus"
              current={subs.daily.current}
              reference={subs.daily.yearAgo}
              referenceLabel="Même période l'an dernier"
              currentLabel="Abonnés"
              aggregate="last"
              dots
              value={(point) => point.activeSubscribers}
              formatValue={formatInteger}
              formatAxis={formatCompact}
            />
            <SalesTrendChart
              title="Essais en cours par jour"
              subtitle="Essais gratuits démarrés dans l'app et pas encore terminés le soir — hors abonnements vendus sur le site"
              current={subs.daily.current}
              reference={subs.daily.yearAgo}
              referenceLabel="Même période l'an dernier"
              currentLabel="Essais"
              aggregate="last"
              dots
              value={(point) => point.trialsActive ?? 0}
              formatValue={formatInteger}
              formatAxis={formatCompact}
            />
          </div>
        </div>
      )}

      {subs?.renewalMonths && subsTotals && (
        <div className={subscriptionsQuery.isFetching ? 'is-refetching' : undefined}>
          <h2 className="section-heading">Renouvellements année 2</h2>
          <div className="kpi-row">
            <StatTile
              label="Échéances"
              value={formatInteger(subsTotals.renewalsDue ?? 0)}
              deltas={subsVolume((t) => t.renewalsDue ?? 0, formatInteger, null)}
              hint="annuels arrivés au bout de leur première année payante sur la période"
            />
            <StatTile
              label="Renouvelés"
              value={formatInteger(subsTotals.renewalsRenewed ?? 0)}
              deltas={subsVolume((t) => t.renewalsRenewed ?? 0, formatInteger)}
              hint={`${money(subsTotals.renewalsRevenue ?? 0)} encaissés sur ces renouvellements`}
            />
            <StatTile
              label="Taux de renouvellement"
              value={subsTotals.renewalRatePercent != null ? formatPercent(subsTotals.renewalRatePercent) : '—'}
              deltas={subsRate((t) => t.renewalRatePercent ?? null, true)}
              hint={
                (subsTotals.renewalsPending ?? 0) > 0
                  ? `renouvelés ÷ échéances à l'issue connue — ${formatInteger(subsTotals.renewalsPending)} encore en relance, hors calcul`
                  : 'renouvelés ÷ échéances à l\'issue connue'
              }
            />
            <StatTile
              label="Résiliés avant"
              value={formatInteger(subsTotals.renewalsCanceled ?? 0)}
              deltas={subsVolume((t) => t.renewalsCanceled ?? 0, formatInteger, false)}
              hint="résiliation demandée avant l'échéance"
            />
            <StatTile
              label="Échecs de paiement"
              value={formatInteger(subsTotals.renewalsFailed ?? 0)}
              deltas={subsVolume((t) => t.renewalsFailed ?? 0, formatInteger, false)}
              hint="échéance passée depuis plus de 35 jours (ou abonnement terminé), sans paiement"
            />
            <StatTile
              label="1re année offerte"
              value={`${formatInteger(subsTotals.renewalsRenewedFreeFirstYear ?? 0)} / ${formatInteger(subsTotals.renewalsDueFreeFirstYear ?? 0)}`}
              hint="renouvelés / échéances, parmi les abonnés qui n'avaient rien payé la première année (coupon, code du site)"
            />
          </div>
          <div className="chart-grid">
            <RenewalMonthsTable months={subs.renewalMonths} currency={currency} />
          </div>
        </div>
      )}

      {subs && (
        <>
          <h2 className="section-heading">
            Actives subscriptions by period &amp; country
            <span className="section-heading__sub">abonnements devenus payants sur la période, encore actifs à sa fin</span>
          </h2>
          <div className="chart-grid">
            <DonutChart
              title="Par période de facturation"
              subtitle="Part d'annuels et de mensuels parmi les abonnements de la période"
              slices={[
                { key: 'year', label: 'Annuel', value: periodByInterval('year'), color: CATEGORICAL_COLORS[0] },
                { key: 'month', label: 'Mensuel', value: periodByInterval('month'), color: CATEGORICAL_COLORS[1] },
              ]}
              formatValue={formatInteger}
              emptyMessage="Aucun nouvel abonnement sur cette période."
            />
            <DonutChart
              title="Par pays"
              subtitle={`Pays de facturation — ${TOP_COUNTRIES} premiers pays et les autres`}
              slices={subscriberCountrySlices}
              formatValue={formatInteger}
              emptyMessage="Aucun nouvel abonnement sur cette période."
            />
          </div>
        </>
      )}

      {subs?.siteSales && (
        <div className={subscriptionsQuery.isFetching ? 'is-refetching' : undefined}>
          <h2 className="section-heading">Abonnement vendu sur le site</h2>
          <div className="kpi-row">
            <StatTile
              label="Vendus sur le site"
              value={formatInteger(subsTotals?.siteUnits ?? 0)}
              deltas={subsVolume((t) => t.siteUnits ?? 0, formatInteger)}
              hint="« Abonnement 1 an » vendus sur Shopify sur la période, tous tests confondus"
            />
            <StatTile
              label="CA site TTC"
              value={money(subsTotals?.siteRevenue ?? 0)}
              deltas={subsVolume((t) => t.siteRevenue ?? 0, money)}
              hint="le jour de la commande, remises déduites — compté en abonnements, retiré de Shopify sur le Résumé"
            />
            <StatTile
              label="Depuis le lancement"
              value={formatInteger(subs.siteSales.allTime.units)}
              hint={`abonnements vendus sur le site${subs.siteSales.allTime.since ? ` depuis le ${formatDayShort(subs.siteSales.allTime.since)}` : ''}, quelle que soit la période — ${money(subs.siteSales.allTime.revenue)} TTC`}
            />
          </div>

          {subs.siteSales.tests.map((test) => (
            <section key={test.key} aria-label={test.label}>
              <h3 className="section-heading section-heading--minor">
                {test.label}
                <span className="section-heading__sub">
                  {test.testFrom ? `du ${formatDayShort(test.testFrom)}` : ''}
                  {test.testTo ? ` au ${formatDayShort(test.testTo)}` : ' — en cours'}
                  {` · chiffres du ${formatDayShort(test.from)} au ${formatDayShort(test.to)}`}
                </span>
              </h3>
              <div className="kpi-row">
                <StatTile
                  label="Vendus"
                  value={formatInteger(test.units)}
                  hint={
                    (test.variants.length > 0
                      ? test.variants.map((v) => `site ${v.site} : ${formatInteger(v.units)}`).join(' · ') + ' — '
                      : '') + `${test.detail} ; offerts exclus`
                  }
                />
                <StatTile
                  label="CA TTC"
                  value={money(test.revenue)}
                  hint="le jour de la commande, remises déduites"
                />
                <StatTile
                  label="Prix moyen payé (TTC)"
                  value={test.averagePrice !== null ? money(test.averagePrice) : '—'}
                  hint={test.stripeAnnualPrice !== null ? `annuel Stripe : ${money(test.stripeAnnualPrice)}` : undefined}
                />
                <StatTile
                  label="Attache au kit"
                  value={test.attachRatePercent !== null ? formatPercent(test.attachRatePercent) : '—'}
                  hint={
                    (test.variants.length > 0
                      ? test.variants
                          .map((v) => `site ${v.site} (${v.price !== null ? money(v.price) : '—'}) : ${v.attachRatePercent !== null ? formatPercent(v.attachRatePercent) : '—'}`)
                          .join(' · ') + ' — '
                      : '') +
                    `${formatInteger(test.kitOrdersWithSubscription)} commandes de kit avec l'abonnement sur ${formatInteger(test.kitOrders)} commandes de kit à qui il a été proposé (${test.detail})`
                  }
                />
                <StatTile
                  label="Codes activés"
                  value={formatInteger(test.activations)}
                  hint={`codes VIRTUOSO utilisés dans Stripe pendant le test — ${formatInteger(test.activeAtEnd)} abonnés du site actifs (essai terminé) au ${formatDayShort(test.to)}`}
                />
              </div>
              <div className="chart-grid">
                <SiteSubscriptionPrices site={test} currency={currency} />
              </div>
            </section>
          ))}
        </div>
      )}
      </>
      )}

      {view === 'amazon' && (
      <>
      {amazon && !amazonLive && (
        <p className="notice">
          Ventes Amazon pas encore branchées : aucune synchronisation n'a abouti. Vérifier le conteneur{' '}
          <code>sync_amazon</code> (<code>docker compose logs sync_amazon</code>) et ses identifiants
          Selling Partner API.
        </p>
      )}
      {amazonLive && !amazon?.meta.historyImported && (
        <p className="notice">Import de l'historique Amazon en cours : comparaisons incomplètes.</p>
      )}
      {amazonLive && amazon?.meta.lastSyncFailed && (
        <p className="notice notice--error" role="alert">
          Le dernier passage du sync Amazon a échoué. Chiffres du {formatInstant(amazon.meta.lastSyncAt)}.
        </p>
      )}
      {amazon && amazon.meta.missingExchangeRates > 0 && (
        <p className="notice notice--error" role="alert">
          {formatInteger(amazon.meta.missingExchangeRates)} lignes de commande n'ont pas encore de taux de change :
          CA sous-estimé. Le prochain passage du sync Amazon les complète.
        </p>
      )}

      {amazonLive && amazon && (
        <>
          <h2 className="section-heading">Ventes</h2>
          <div className="kpi-row">
            <StatTile
              label="CA Amazon TTC"
              value={money(amazonTotals?.revenue ?? 0)}
              deltas={amazonDeltas((t) => t.revenue, money)}
              hint="articles + port − promotions, hors annulations"
            />
            <StatTile
              label="CA Amazon HT"
              value={money(amazonTotals?.revenueExclTax ?? 0)}
              deltas={amazonDeltas((t) => t.revenueExclTax, money)}
              hint={`${money(amazonTotals?.taxes ?? 0)} de TVA indiquée par Amazon`}
            />
            <StatTile
              label="Commandes"
              value={formatInteger(amazonTotals?.orders ?? 0)}
              deltas={amazonDeltas((t) => t.orders, formatInteger)}
              hint="hors annulations"
            />
            <StatTile
              label="Unités vendues"
              value={formatInteger(amazonTotals?.units ?? 0)}
              deltas={amazonDeltas((t) => t.units, formatInteger)}
              hint={`dont ${formatInteger(amazonTotals?.kits ?? 0)} kits Piano LED`}
            />
            <StatTile
              label="Panier moyen (TTC)"
              value={amazonTotals && amazonTotals.orders > 0 ? formatMoney(amazonTotals.revenue / amazonTotals.orders, currency, true) : '—'}
              deltas={amazonDeltas((t) => (t.orders > 0 ? t.revenue / t.orders : null), (v) => formatMoney(v, currency, true))}
              hint="CA TTC ÷ commandes"
            />
            <StatTile
              label="Amazon Business (TTC)"
              value={money(amazonTotals?.businessRevenue ?? 0)}
              deltas={amazonDeltas((t) => t.businessRevenue, money)}
              hint="commandes de professionnels"
            />
          </div>

          <div className="chart-grid">
            <SalesTrendChart
              title="CA Amazon (TTC)"
              subtitle="TTC, converti en euros, en heure de Paris"
              current={amazon.daily.current}
              reference={amazonReference ?? []}
              referenceLabel={referenceLabel}
              value={amazonRevenueOf}
              formatValue={money}
              formatAxis={moneyAxis}
            />
            <DonutChart
              title="Par marketplace (TTC)"
              subtitle="CA TTC de la période, en euros"
              slices={amazonMarketplaceSlices}
              formatValue={money}
            />
            <TopProductsTable
              rows={amazon.topProducts.map((row) => ({ productKey: row.asin, title: row.title, current: row.current, previous: row.previous, yearAgo: row.yearAgo }))}
              currency={currency}
              comparisonsOverlap={overlap}
            />
          </div>
        </>
      )}

      <h2 className="section-heading">Publicité Amazon</h2>
      {amazonAdsBlock}
      </>
      )}

      {view === 'ads' && (
      <>
      <fieldset className="filters platform-picker">
        <legend className="field__label">Plateformes affichées</legend>
        {AD_PLATFORMS.map((platform) => (
          <label key={platform} className="platform-picker__option">
            <input
              type="checkbox"
              checked={platforms.includes(platform)}
              onChange={(event) => togglePlatform(platform, event.target.checked)}
            />
            {AD_PLATFORM_LABELS[platform]}
            {platform === 'amazon' && !amazonAdsLive && <span className="platform-picker__hint">pas encore branché</span>}
            {platform === 'google' && !googleAdsLive && <span className="platform-picker__hint">en attente de l'accès API</span>}
          </label>
        ))}
      </fieldset>

      {withMeta && (
      <>
      {ads && !ads.meta.lastSyncAt && (
        <p className="notice">
          Aucune synchronisation Meta n'a encore abouti. Vérifier que le conteneur <code>sync_meta</code>{' '}
          tourne (<code>docker compose logs sync_meta</code>).
        </p>
      )}
      {ads?.meta.lastSyncAt && !ads.meta.historyImported && (
        <p className="notice">Import de l'historique Meta en cours : comparaisons incomplètes.</p>
      )}
      {ads?.meta.lastSyncFailed && (
        <p className="notice notice--error" role="alert">
          Le dernier passage du sync Meta a échoué. Les chiffres sont ceux du {formatInstant(ads.meta.lastSyncAt)}.
        </p>
      )}
      {ads && ads.meta.missingExchangeRates > 0 && (
        <p className="notice notice--error" role="alert">
          {formatInteger(ads.meta.missingExchangeRates)} journées de campagne n'ont pas encore de taux de
          change : les dépenses affichées sont sous-estimées. Le prochain passage du sync Meta les complète.
        </p>
      )}
      {ads?.meta.accountTimezone && ads.meta.accountTimezone !== 'Europe/Paris' && (
        <p className="notice">
          Le compte publicitaire est réglé sur le fuseau {ads.meta.accountTimezone} : ses journées ne
          coïncident pas exactement avec celles de la boutique (heure de Paris).
        </p>
      )}

      </>
      )}

      {platforms.length === 0 && (
        <p className="notice">Cochez au moins une plateforme pour afficher le rendement publicitaire.</p>
      )}

      {platforms.length > 0 && (
        <>
          <h2 className="section-heading">
            Rendement
            <span className="section-heading__sub">{selectedPlatformsLabel}</span>
          </h2>
          <div className="kpi-row">
            <StatTile
              label="Dépenses pub (HT)"
              value={money(platformNow.spend)}
              deltas={platformDeltas((t) => t.spend, money, null)}
              hint={selectedPlatformsLabel}
            />
            <StatTile
              label="Ventes attribuées (HT)"
              value={money(platformNow.attributed)}
              deltas={platformDeltas((t) => t.attributed, money, true)}
              hint="selon chaque plateforme"
            />
            <StatTile
              label="ROAS attribué (HT)"
              value={platformNow.roasAttributed != null ? formatRatio(platformNow.roasAttributed) : '—'}
              deltas={platformDeltas((t) => t.roasAttributed, formatRatio, true)}
              hint="ventes attribuées ÷ dépenses"
            />
            <StatTile
              label="MER (HT)"
              value={platformNow.roasGlobal != null ? formatRatio(platformNow.roasGlobal) : '—'}
              deltas={platformDeltas((t) => t.roasGlobal, formatRatio, true)}
              hint={`Marketing Efficiency Ratio : CA réel ${[withMeta || withGoogleAds ? 'Shopify' : null, withAmazonAds ? 'Amazon' : null].filter(Boolean).join(' + ')} ÷ dépenses`}
            />
            <StatTile
              label="CA HT − pub"
              value={money(platformNow.afterAds)}
              deltas={platformDeltas((t) => t.afterAds, money, true)}
              hint="CA des canaux − dépenses pub"
            />
            <StatTile
              label="Coût par commande (HT)"
              value={platformNow.costPerOrder != null ? formatMoney(platformNow.costPerOrder, currency, true) : '—'}
              deltas={platformDeltas((t) => t.costPerOrder, (x) => formatMoney(x, currency, true), false)}
              hint={`dépenses ÷ ${formatInteger(platformNow.orders)} commandes des canaux`}
            />
          </div>
          <div className="chart-grid">
            <SalesTrendChart
              className="chart-grid__wide"
              title="Dépenses pub par jour (HT)"
              subtitle={`${selectedPlatformsLabel}, en euros`}
              current={platformSpendSeries('current')}
              reference={platformSpendSeries(chartReference === 'yoy' ? 'yearAgo' : 'previous')}
              referenceLabel={referenceLabel}
              value={platformSpendOf}
              formatValue={money}
              formatAxis={moneyAxis}
            />
          </div>
        </>
      )}

      {platforms.length > 0 && (
        <>
          <h2 className="section-heading">
            Ads KPIs
            <span className="section-heading__sub">{selectedPlatformsLabel}</span>
          </h2>
          <div className="kpi-row">
            <StatTile label="Cost (HT)" value={money(kpiNow.cost)} deltas={kpiDeltas((t) => t.cost, money, null)} hint="dépenses, en euros" />
            <StatTile
              label="Impressions"
              value={formatInteger(kpiNow.impressions)}
              deltas={kpiDeltas((t) => t.impressions, formatInteger, true)}
            />
            <StatTile label="Clicks" value={formatInteger(kpiNow.clicks)} deltas={kpiDeltas((t) => t.clicks, formatInteger, true)} />
            <StatTile
              label="CTR %"
              value={kpiNow.ctr != null ? formatPercent(kpiNow.ctr) : '—'}
              deltas={kpiDeltas((t) => t.ctr, formatPercent, true, true)}
              hint="clics ÷ impressions"
            />
            <StatTile
              label="Conversions"
              value={formatDecimal(kpiNow.conversions)}
              deltas={kpiDeltas((t) => t.conversions, formatDecimal, true)}
              hint="achats attribués"
            />
            <StatTile
              label="CVR %"
              value={kpiNow.cvr != null ? formatPercent(kpiNow.cvr) : '—'}
              deltas={kpiDeltas((t) => t.cvr, formatPercent, true, true)}
              hint="conversions ÷ clics"
            />
            <StatTile
              label="Conversion Value (TTC)"
              value={money(kpiNow.conversionValue)}
              deltas={kpiDeltas((t) => t.conversionValue, money, true)}
              hint="valeur des achats attribués"
            />
            <StatTile
              label="ROAS (HT)"
              value={kpiNow.roas != null ? formatRatio(kpiNow.roas) : '—'}
              deltas={kpiDeltas((t) => t.roas, formatRatio, true)}
              hint="conversion value ÷ cost"
            />
            <StatTile
              label="CPC (HT)"
              value={kpiNow.cpc != null ? formatMoney(kpiNow.cpc, currency, true) : '—'}
              deltas={kpiDeltas((t) => t.cpc, (x) => formatMoney(x, currency, true), false)}
              hint="cost ÷ clics"
            />
            <StatTile
              label="CPM (HT)"
              value={kpiNow.cpm != null ? formatMoney(kpiNow.cpm, currency, true) : '—'}
              deltas={kpiDeltas((t) => t.cpm, (x) => formatMoney(x, currency, true), false)}
              hint="cost pour 1 000 impressions"
            />
            <StatTile
              label="CPA (HT)"
              value={kpiNow.cpa != null ? formatMoney(kpiNow.cpa, currency, true) : '—'}
              deltas={kpiDeltas((t) => t.cpa, (x) => formatMoney(x, currency, true), false)}
              hint="cost ÷ conversions"
            />
          </div>
          <div className="chart-grid">
            <CostRoasChart points={costValueSeries} currency={currency} subtitle={selectedPlatformsLabel} />
            <AdsDailyTable withMeta={withMeta} withAmazon={withAmazonAds && amazonAdsLive} withGoogle={withGoogleAds && googleAdsLive} currency={currency} />
          </div>
        </>
      )}

      {platforms.length > 0 && (
        <>
          <h2 className="section-heading">
            Performances by platform
            <span className="section-heading__sub">{selectedPlatformsLabel}</span>
          </h2>
          <div className="chart-grid">
            <AdsKpiTable rows={kpiRowsFor('current')} currency={currency} />
            <AdsPlatformChart
              period={dataPeriod(period, 'live')}
              withMeta={withMeta}
              withAmazon={withAmazonAds && amazonAdsLive}
              withGoogle={withGoogleAds && googleAdsLive}
              currency={currency}
            />
          </div>
        </>
      )}

      {platforms.length > 0 && (
        <>
          <h2 className="section-heading">
            Cost, Revenue, ROAS
            <span className="section-heading__sub">{selectedPlatformsLabel}</span>
          </h2>
          <div className="chart-grid chart-grid--thirds">
            <AdsPlatformSplit rows={kpiRowsFor('current')} currency={currency} />
          </div>
        </>
      )}

      {platforms.length > 0 && (
        <>
          <h2 className="section-heading">
            CPC, CPM, CPA
            <span className="section-heading__sub">{selectedPlatformsLabel}</span>
          </h2>
          <div className="chart-grid chart-grid--thirds">
            <AdsCostRatios rows={kpiRowsFor('current')} currency={currency} />
          </div>
        </>
      )}

      </>
      )}

      {/* Rattachement des remboursements : un réglage qu'on touche rarement,
          en bas de page plutôt que dans la barre de filtres. */}
      {showShop && (
      <div className="filters filters--bottom">
        <div className="field">
          <span className="field__label">Remboursements rattachés</span>
          <div className="preset-group" role="group" aria-label="Rattachement des remboursements">
            {REFUND_BASES.map((basis) => (
              <button
                key={basis}
                type="button"
                className="preset"
                aria-pressed={refundBasis === basis}
                onClick={() => setRefundBasis(basis)}
              >
                {REFUND_BASIS_LABELS[basis]}
              </button>
            ))}
          </div>
        </div>
      </div>
      )}
      {showShop && (
      <p className="footnote">
        Source : Shopify, synchronisé toutes les dix minutes. Montants TTC en devise de la boutique,
        rattachés à la date de traitement de la commande (comme les rapports Shopify), journées en
        heure de Paris. Commandes annulées et de test exclues, ainsi que leurs remboursements.
        « Période précédente » : pour une période qui commence un 1er, le même intervalle un mois plus
        tôt ; sinon les jours qui précèdent immédiatement, en même nombre. « L'an dernier » : les
        mêmes dates un an avant. Les courbes sont alignées jour pour jour depuis le début de chaque
        fenêtre.
      </p>
      )}
      {view === 'subscriptions' && (
      <p className="footnote">
        Source : Stripe, synchronisé toutes les dix minutes. CA TTC : MRR au dernier jour de la
        période, comme la tuile MRR du Résumé. CA HT : CA TTC hors TVA française. Retenues (Sacem,
        frais Stripe…) : pourcentage du CA TTC ou HT, réglé sur la page Frais (« Retenues sur le CA des abonnements »). Final HT =
        CA HT − retenues.
        Subscriptions KPIs, définitions du Dashboard Stripe : un abonné actif est un abonnement
        payant à MRR positif (essais et pauses exclus), qui sort des compteurs dès sa demande de
        résiliation. Souscriptions : abonnements devenus payants dans la période. Churn : départs ÷
        (abonnés au début + nouveaux). « Actives à date » : parc au dernier sync, sans comparaison.
        MRR : prix TTC ramenés au mois (un annuel compte pour 1/12e) moins les réductions
        permanentes ; ARR = MRR × 12. Taux comparés en points.
      </p>
      )}
      {view === 'summary' && (
      <p className="footnote">
        Abonnements : Stripe, synchronisé toutes les dix minutes. Un abonné est un abonnement payant —
        il entre dans les compteurs à la fin de son essai gratuit, et un essai abandonné n'est ni un
        abonné ni une résiliation. Le CA abonnements est encaissé (factures payées, à leur date de
        paiement) ; les remboursements Stripe sont toujours déduits à leur date, quel que soit le
        réglage ci-dessus. Abonnés actifs et MRR suivent les définitions du Dashboard Stripe,
        vérifiées contre son export « MRR par abonné » : abonnements actifs ou en retard de
        paiement à MRR positif, essais et facturations en pause exclus, un abonné sortant des
        compteurs dès sa demande de résiliation (même si sa période payée court encore) ; MRR = prix
        ramené au mois (un annuel compte pour 1/12e) moins les réductions permanentes.
        La tuile donne la situation au soir du dernier jour de la période, la mention « en
        direct » celle du dernier sync — c'est elle qui se compare au Dashboard Stripe. Churn, formule de Stripe : départs de la période ÷ (abonnés au début + nouveaux
        abonnés).
        Pénétration : abonnés actifs au dernier jour rapportés aux kits Piano LED vendus sur Shopify
        depuis la création du premier abonnement Stripe (produits dont le titre contient « Piano LED », hors « compatible » ;
        retours déduits). Essais : abonnements démarrés par un essai gratuit ; un essai se termine à
        son échéance ou à sa résiliation si elle survient avant, et il est converti s'il devient
        payant. Taux comparés en points.
      </p>
      )}
      {(view === 'amazon' || view === 'summary') && (
      <p className="footnote">
        Amazon : ventes via la Selling Partner API (rapport de toutes les commandes, sans donnée
        d'acheteur), publicité via l'Amazon Ads API. Montants convertis en euros au taux BCE du jour
        de chaque commande ou dépense. CA = articles + port − promotions, TTC, commandes annulées
        exclues ; les remboursements Amazon ne sont pas encore déduits (ils vivent dans les
        événements financiers d'Amazon). Ventes attribuées selon les fenêtres de la console : 7 jours
        pour Sponsored Products, 14 jours pour Brands et Display. ACoS = dépenses ÷ ventes attribuées ;
        TACoS = dépenses ÷ CA Amazon total, organique compris. Amazon ne conserve qu'environ trois mois
        d'historique publicitaire.
      </p>
      )}
      {(view === 'ads' || view === 'summary') && (
      <p className="footnote">
        Publicité : Meta, synchronisée toutes les dix minutes ; les 28 derniers jours sont relus à
        chaque passage, le temps que Meta finisse d'attribuer ses conversions. Le compte publicitaire
        est facturé en dollars : dépenses et ventes attribuées sont converties en euros au taux de
        référence de la BCE du jour de chaque dépense (dernier taux publié pour un week-end) — le débit
        réel de la banque en diffère de sa marge de change. MER (Marketing Efficiency Ratio) = CA
        boutique net réel ÷ dépenses : il inclut les ventes que la pub n'a pas amenées, c'est une borne
        haute. CPA (Résumé) = dépenses pub toutes plateformes ÷ Piano LED payants vendus (Shopify +
        Amazon, kits à 0 € d'un pack compris, hors commandes gratuites et BtoB). ROAS Meta et ROAS Google Ads (Résumé) = achats que la régie s'attribue × prix HT moyen
        d'un Piano LED sur la période ÷ dépenses de la régie : des kits, pas la valeur déclarée par la
        régie. ROAS Meta et
        CPA Meta = achats attribués par Meta selon la fenêtre d'attribution du compte : la vision de
        Meta, utile pour comparer les campagnes. ROI sur CA = (CA boutique − pub) ÷ pub, sans le coût
        des produits ; ROI sur marge = (CA boutique × taux de marge − pub) ÷ pub. Coûts d'acquisition :
        dépenses pub de la période rapportées aux commandes, aux kits vendus ou aux nouveaux abonnés
        de la même période.
      </p>
      )}
    </SectionShell>
  );
}
